// ========================================
// 光属性カードの固有効果
// ========================================

import {
  getPlayerContext,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  drawCards,
  healLife,
  modifyAttack,
  applyStatusToOwnMonster,
  applyStatusToOpponentMonster,
  applyStatusToAllOpponentMonsters,
  destroyMonster,
} from '../effectHelpers';
import { STATUS_EFFECT_TYPES, statusEffectEngine } from '../statusEffects';
import { hasCategory, createMonsterInstance } from '../../utils/helpers';
import { registerCardTriggers } from '../triggerEngine';
import { continuousEffectEngine } from '../continuousEffects';
import type { CardEffectRegistry } from './index';
import type { MonsterInstance, CardWithId, CardData, PlayerNumber } from '../../types';

/**
 * 光属性カードの固有効果
 */
export const lightCardEffects: CardEffectRegistry = {
  /**
   * C0000062: 光の使徒
   * 基本技：手札から光属性カード1枚を捨てるとレスト状態のSPトークン1つをアクティブにする
   */
  C0000062: (skillText, context) => {
    const { addLog, setPendingHandSelection } = context;
    const { myHand, myRestedSP, setMyHand, setMyGraveyard, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 手札に光属性カードがあるか確認
      const lightCards = myHand.filter(c => c.attribute === '光');
      if (lightCards.length === 0) {
        addLog('手札に光属性カードがありません', 'info');
        return false;
      }

      // レストSPがあるか確認
      if (myRestedSP <= 0) {
        addLog('レスト状態のSPトークンがありません', 'info');
        return false;
      }

      // 手札選択モードに入る
      setPendingHandSelection({
        message: '捨てる光属性カードを選択',
        filter: (card: CardWithId) => card.attribute === '光',
        callback: (selectedCards: CardWithId[]) => {
          if (!selectedCards || selectedCards.length === 0) return;
          const selectedCard = selectedCards[0];
          // 手札から削除して墓地に送る
          setMyHand(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
          setMyGraveyard(prev => [...prev, selectedCard]);

          // レストSPを1つアクティブにする
          setMyRestedSP(prev => prev - 1);
          setMyActiveSP(prev => prev + 1);

          addLog(`「${selectedCard.name}」を捨ててSPを1つアクティブにした`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });

      return true;
    }
    return false;
  },

  /**
   * C0000059: 光の騎士
   * 【召喚時】デッキから《光の》魔法カード1枚を手札に加える
   */
  C0000059: (skillText, context) => {
    if (skillText.includes('【召喚時】')) {
      return searchCard(context, (card) => {
        return card.type === 'magic' && !!!!card.name && card.name.includes('光の');
      }) !== null;
    }
    return false;
  },

  /**
   * C0000060: 聖なる導師
   * 基本技：場にいる光属性モンスター1体の攻撃力をターン終了時まで600アップ
   */
  C0000060: (skillText, context) => {
    if (context.skillType === 'basic') {
      return modifyAttack(context, 600, 0, false, false);
    }
    return false;
  },

  /**
   * C0000121: 聖域の灯守兵
   * 【召喚時】自分の手札から光属性モンスター1体を公開し、デッキに戻す。その後、1枚ドローする
   */
  C0000121: (skillText, context) => {
    const { addLog } = context;
    const { myHand, setMyHand, setMyDeck } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      const lightMonster = myHand.find(card =>
        card.type === 'monster' && card.attribute === '光'
      );

      if (lightMonster) {
        // 手札からデッキに戻す
        setMyHand(prev => prev.filter(c => c.uniqueId !== lightMonster.uniqueId));
        setMyDeck(prev => [...prev, lightMonster]);
        addLog(`手札から「${lightMonster.name}」をデッキに戻した`, 'info');

        // 1枚ドロー
        drawCards(context, 1);
        return true;
      } else {
        addLog('手札に光属性モンスターがありません', 'info');
        return false;
      }
    }
    return false;
  },

  /**
   * C0000122: 聖域の戦士
   * 基本技: 相手モンスター1体に400ダメージ与える（ターンに1度）。
   */
  C0000122: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手モンスターを検索
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('聖域の戦士の基本技: 相手フィールドにモンスターがいません', 'info');
        return false;
      }

      const applyDamage = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          if (newField[targetIndex]) {
            const target = newField[targetIndex];
            const newHp = target.currentHp - 400;
            if (newHp <= 0) {
              setOpponentGraveyard(p => [...p, target]);
              addLog(`聖域の戦士の基本技: ${target.name}に400ダメージで破壊！`, 'damage');
              newField[targetIndex] = null;
            } else {
              newField[targetIndex] = {
                ...target,
                currentHp: newHp,
              };
              addLog(`聖域の戦士の基本技: ${target.name}に400ダメージ！`, 'damage');
            }
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (opponentMonsters.length === 1) {
        applyDamage(opponentMonsters[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '400ダメージを与えるモンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyDamage(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyDamage(opponentMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000211: フルーツ・マリオネット・アップル
   * 【召喚時】デッキからコスト3以下のプラントモンスター1体を墓地に送る
   * 基本技：自分モンスターの凍結などの状態異常を回復する
   */
  C0000211: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myDeck, setMyDeck, setMyGraveyard, myField, setMyField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      const plantCard = myDeck.find(card =>
        card.type === 'monster' &&
        card.cost <= 3 &&
        hasCategory(card, '【プラント】')
      );

      if (plantCard) {
        setMyDeck(prev => prev.filter(c => c.uniqueId !== plantCard.uniqueId));
        setMyGraveyard(prev => [...prev, plantCard]);
        addLog(`デッキから「${plantCard.name}」を墓地に送った`, 'info');
        return true;
      } else {
        addLog('デッキにコスト3以下のプラントモンスターがありません', 'info');
        return false;
      }
    }

    // 基本技：自分モンスターの状態異常を回復
    if (context.skillType === 'basic') {
      const validTargets = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null && monster.statusEffects && monster.statusEffects.length > 0);

      if (validTargets.length === 0) {
        addLog('状態異常を持つ自分のモンスターがいません', 'info');
        return false;
      }

      setPendingMonsterTarget({
        message: '状態異常を回復するモンスターを選択',
        targetPlayer: 'self',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          setMyField(prev => {
            const newField = [...prev];
            if (newField[selectedIndex]) {
              const removedEffects = newField[selectedIndex].statusEffects.map(e => e.type).join(', ');
              newField[selectedIndex] = {
                ...newField[selectedIndex],
                statusEffects: [],
              };
              addLog(`${newField[selectedIndex].name}の状態異常(${removedEffects})を回復した`, 'info');
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }
    return false;
  },

  /**
   * C0000212: フルーツ・マリオネット・オレンジ
   * 【召喚時】デッキから《フルーツ・マリオネット》モンスター1体を手札に加える
   * 基本技：相手モンスター1体の攻撃力を400ダウン
   */
  C0000212: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      return searchCard(context, (card) => {
        return !!card.name && card.name.includes('フルーツ・マリオネット');
      }) !== null;
    }

    // 基本技：相手モンスター1体の攻撃力を400ダウン
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return false;
      }

      setPendingMonsterTarget({
        message: '攻撃力を400下げるモンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          setOpponentField(prev => {
            const newField = [...prev];
            if (newField[selectedIndex]) {
              newField[selectedIndex] = {
                ...newField[selectedIndex],
                currentAttack: Math.max(0, newField[selectedIndex].currentAttack - 400),
              };
              addLog(`${newField[selectedIndex].name}の攻撃力を400下げた（現在: ${newField[selectedIndex].currentAttack}）`, 'info');
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }
    return false;
  },

  /**
   * C0000213: フルーツ・マリオネット・バナナ
   * 【自壊時】自分モンスター全てのHPを600回復。（トリガーで実装済み）
   * 基本技：禁忌カード以外の相手モンスター1体を指定し、そのコントロールをターン終了時まで奪う
   *        （この効果で奪ったモンスターは攻撃できない）
   *        場に光属性【プラント】モンスターが2体以上いる場合、次のターン終了時まで継続
   */
  C0000213: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField } = getPlayerContext(context);

    // 基本技：コントロール奪取
    if (context.skillType === 'basic') {
      // 自分の場の空きスロットを確認
      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('自分の場に空きがありません', 'info');
        return false;
      }

      // 禁忌カード以外の相手モンスターを検索
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null && !monster.forbidden);

      if (validTargets.length === 0) {
        addLog('奪えるモンスターがいません', 'info');
        return false;
      }

      // 光属性【プラント】モンスターの数をカウント
      const lightPlantCount = myField.filter(m =>
        m !== null && m.attribute === '光' && hasCategory(m, '【プラント】')
      ).length;
      const extendedDuration = lightPlantCount >= 2;

      setPendingMonsterTarget({
        message: 'コントロールを奪うモンスターを選択',
        targetPlayer: 'opponent',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          const stolenMonster = opponentField[selectedIndex];
          if (!stolenMonster) return;

          // 相手の場から除去
          setOpponentField(prev => {
            const newField = [...prev];
            newField[selectedIndex] = null;
            return newField;
          });

          // 自分の場に追加（攻撃不可、コントロール奪取フラグ付き）
          setMyField(prev => {
            const newField = [...prev];
            const slot = newField.findIndex(s => s === null);
            if (slot !== -1) {
              newField[slot] = {
                ...stolenMonster,
                canAttack: false,
                controlStolen: true,
                originalOwner: stolenMonster.owner,
                controlReturnTurn: extendedDuration ? 2 : 1, // 1=このターン終了時、2=次のターン終了時
              };
            }
            return newField;
          });

          const duration = extendedDuration ? '次のターン終了時' : 'ターン終了時';
          addLog(`${stolenMonster.name}のコントロールを${duration}まで奪った（攻撃不可）`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }
    return false;
  },

  /**
   * C0000214: フルーツ・マリオネット・グレープ
   * 【召喚時】自分の墓地の［プラント］モンスター1体を場に戻す（攻撃力は半分）
   * 基本技：このカードのHPは場にいる［プラント］モンスター1体×400回復する（上限HP3000）
   */
  C0000214: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      return reviveFromGraveyard(context, (card) => {
        return hasCategory(card, '【プラント】');
      }, true);
    }

    // 基本技：プラント数×400回復（上限3000）
    if (context.skillType === 'basic') {
      const plantCount = myField.filter(m => m !== null && hasCategory(m, '【プラント】')).length;
      const healAmount = plantCount * 400;

      if (monsterIndex === undefined || !myField[monsterIndex]) {
        addLog('対象のモンスターが見つかりません', 'info');
        return false;
      }

      setMyField(prev => {
        const newField = [...prev];
        const monster = newField[monsterIndex];
        if (monster) {
          const newHp = Math.min(monster.currentHp + healAmount, 3000);
          const actualHeal = newHp - monster.currentHp;
          newField[monsterIndex] = {
            ...monster,
            currentHp: newHp,
            maxHp: Math.max(monster.maxHp ?? monster.hp ?? newHp, newHp),
          };
          addLog(`${monster.name}のHPを${actualHeal}回復した（プラント${plantCount}体×400、現在HP: ${newHp}）`, 'info');
        }
        return newField;
      });
      return true;
    }
    return false;
  },

  /**
   * C0000215: フルーツ・マリオネット・メロン王
   * 【常時】場にいる［プラント］モンスター1体につき攻撃力400アップ（continuousEffectsで実装済み）
   * 基本技：相手の場にいるモンスター1体の効果をターン終了時まで無効化し、攻撃力を半分にする
   */
  C0000215: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    // 基本技：効果無効+攻撃力半減
    if (context.skillType === 'basic') {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return false;
      }

      setPendingMonsterTarget({
        message: '効果を無効化して攻撃力を半減するモンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          setOpponentField(prev => {
            const newField = [...prev];
            if (newField[selectedIndex]) {
              const monster = newField[selectedIndex];
              const newAttack = Math.floor(monster.currentAttack / 2);
              // SILENCE状態異常を付与
              statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.SILENCE, {
                duration: 0, // このターン終了時まで
                source: 'C0000215',
                sourceName: 'フルーツ・マリオネット・メロン王',
              });
              newField[selectedIndex] = {
                ...monster,
                currentAttack: newAttack,
              };
              addLog(`${monster.name}の効果を無効化し、攻撃力を半分（${newAttack}）にした`, 'info');
            }
            return newField;
          });
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }
    return false;
  },

  /**
   * C0000234: 撮影会のリリカ
   * 【常時】相手の魔法カードの発動をターンに1度無効化（continuousEffectsで実装済み）
   * 基本技：場にいる限り一度だけ、自分の墓地の《ゴシック》モンスター1体を場に戻す（攻撃力半分）
   */
  C0000234: (skillText, context) => {
    const { addLog, monsterIndex, setPendingGraveyardSelection, summoningSickness = true } = context;
    const { myField, setMyField, myGraveyard, setMyGraveyard } = getPlayerContext(context);

    // 基本技：墓地からゴシックモンスター蘇生（一度だけ）
    if (context.skillType === 'basic') {
      // このカードが既にこの効果を使用したかチェック
      const thisCard = myField[monsterIndex];
      if (thisCard && thisCard.usedReviveEffect) {
        addLog('この効果は既に使用しました', 'info');
        return false;
      }

      // 空きスロットを確認
      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('場に空きがありません', 'info');
        return false;
      }

      // 墓地から《ゴシック》モンスターを検索
      const gothicMonsters = myGraveyard.filter(card =>
        card.type === 'monster' && !!card.name && card.name.includes('ゴシック')
      );

      if (gothicMonsters.length === 0) {
        addLog('墓地に《ゴシック》モンスターがいません', 'info');
        return false;
      }

      setPendingGraveyardSelection({
        message: '蘇生する《ゴシック》モンスターを選択',
        cards: gothicMonsters,
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards.length === 0) return;

          const selectedCard = selectedCards[0];

          // 墓地から除去
          setMyGraveyard(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

          // 場に蘇生（攻撃力半分）
          setMyField(prev => {
            const newField = [...prev];
            // 使用フラグを立てる
            if (newField[monsterIndex]) {
              newField[monsterIndex] = {
                ...newField[monsterIndex],
                usedReviveEffect: true,
              };
            }

            const slot = newField.findIndex(s => s === null);
            if (slot !== -1) {
              newField[slot] = {
                ...selectedCard,
                currentHp: selectedCard.hp ?? 0,
                maxHp: selectedCard.hp ?? 0,
                currentAttack: Math.floor((selectedCard.attack ?? 0) / 2),
                canAttack: !summoningSickness, // 召喚酔い設定に依存
                charges: [],
                statusEffects: [],
                usedSkillThisTurn: false,
                owner: context.currentPlayer,
              };
            }
            return newField;
          });

          addLog(`${selectedCard.name}を攻撃力半分（${Math.floor((selectedCard.attack ?? 0) / 2)}）で蘇生した`, 'info');
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }
    return false;
  },

  /**
   * C0000235: プリンセス狂いのリリカ
   * 【召喚時】デッキから《プリティ☆プリンセス》魔法カード1枚を発動する（コストなし）
   * 基本技：《プリティ☆プリンセス》魔法カードをデッキから手札に加える
   */
  C0000235: (skillText, context) => {
    const { addLog } = context;

    if (skillText.includes('【召喚時】')) {
      const foundCard = searchCard(context, (card) => {
        return card.type === 'magic' && !!card.name && card.name.includes('プリティ☆プリンセス');
      });

      if (foundCard) {
        addLog(`魔法カード「${foundCard.name}」を発動（コストなし）`, 'info');
        // TODO: 魔法効果の実行（現在は簡易実装）
        return true;
      }
      return false;
    }

    // 基本技：《プリティ☆プリンセス》魔法カードをサーチ
    if (context.skillType === 'basic') {
      return searchCard(context, (card) => {
        return card.type === 'magic' && !!card.name && card.name.includes('プリティ☆プリンセス');
      }) !== null;
    }
    return false;
  },

  /**
   * C0000318: 雷鳴の使者
   * 【召喚時】相手モンスター1体の攻撃力をターン終了時まで500下げる
   * 基本技：自身の攻撃力を500下げて、500ダメージを相手モンスター1体に与える
   */
  C0000318: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいないため効果なし', 'info');
        return false;
      }

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        addLog(`${validTargets[0].monster!.name}の攻撃力を500下げた！`, 'info');
        return modifyAttack(context, -500, validTargets[0].index, true, false);
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '攻撃力を500下げる相手モンスターを選択',
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            const target = opponentField[targetIndex];
            if (target) {
              addLog(`${target.name}の攻撃力を500下げた！`, 'info');
            }
            modifyAttack(context, -500, targetIndex, true, false);
          },
          selectionOwner: context.currentPlayer,
        });
      }
      return true;
    }

    // 基本技：自身の攻撃力を500下げて、500ダメージを相手モンスター1体に与える
    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('モンスターが存在しません', 'info');
        return false;
      }

      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        // 自身の攻撃力を500下げる
        setMyField(prev => {
          const newField = [...prev];
          const monster = newField[monsterIndex];
          if (monster) {
            const newAtk = Math.max(0, monster.currentAttack - 500);
            newField[monsterIndex] = {
              ...monster,
              currentAttack: newAtk,
              attack: Math.max(0, (monster.attack ?? 0) - 500),
            };
          }
          return newField;
        });

        // 相手モンスターに500ダメージ
        setOpponentField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const newHp = target.currentHp - 500;
            addLog(`雷鳴の使者の基本技: 攻撃力-500、${target.name}に500ダメージ！`, 'damage');
            if (newHp <= 0) {
              addLog(`${target.name}は破壊された！`, 'damage');
              setOpponentGraveyard(prev => [...prev, target]);
              newField[targetIndex] = null;
            } else {
              newField[targetIndex] = { ...target, currentHp: newHp };
            }
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyEffect(validTargets[0].index);
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: `500ダメージを与える相手モンスターを選択`,
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applyEffect(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
      }
      return true;
    }
    return false;
  },

  /**
   * C0000322: 嵐の雛雷鳥
   * 【召喚時】デッキから《雷》または《嵐》と名の付くカード1枚を手札に加える
   */
  C0000322: (skillText, context) => {
    if (skillText.includes('【召喚時】')) {
      return searchCard(context, (card) => {
        return !!card.name && (card.name.includes('雷') || card.name.includes('嵐'));
      }) !== null;
    }
    return false;
  },

  /**
   * C0000056: 輝聖女ルミナス
   * 基本技: このカードのHPを300回復。
   * 上級技: 手札から光属性モンスター1体をコストなしで召喚可能。
   */
  C0000056: (skillText, context) => {
    const { addLog, monsterIndex, setPendingHandSelection, summoningSickness = true } = context;
    const { currentPlayer } = getPlayerContext(context);
    const { myField, myHand, setMyField, setMyHand } = getPlayerContext(context);

    // 基本技: HPを300回復
    if (context.skillType === 'basic') {
      if (monsterIndex === undefined || !myField[monsterIndex]) {
        addLog('対象のモンスターが見つかりません', 'info');
        return false;
      }

      const monster = myField[monsterIndex];
      const healAmount = Math.min(300, (monster.maxHp ?? monster.hp ?? monster.currentHp) - monster.currentHp);

      if (healAmount <= 0) {
        addLog(`${monster.name}のHPは既に最大です`, 'info');
        return false;
      }

      setMyField(prev => {
        const newField = [...prev];
        const target = newField[monsterIndex];
        if (target) {
          newField[monsterIndex] = {
            ...target,
            currentHp: target.currentHp + healAmount,
          };
        }
        return newField;
      });

      addLog(`輝聖女ルミナスの基本技: HPを${healAmount}回復！`, 'heal');
      return true;
    }

    // 上級技: 手札から光属性モンスター1体をコストなしで召喚
    if (context.skillType === 'advanced') {
      // 手札に光属性モンスターがあるか確認
      const lightMonsters = myHand.filter(card =>
        card.type === 'monster' && card.attribute === '光'
      );

      if (lightMonsters.length === 0) {
        addLog('手札に光属性モンスターがありません', 'info');
        return false;
      }

      // フィールドに空きがあるか確認
      const emptySlotIndex = myField.findIndex(slot => slot === null);
      if (emptySlotIndex === -1) {
        addLog('フィールドに空きがありません', 'info');
        return false;
      }

      // 手札選択モードに入る
      setPendingHandSelection({
        message: '特殊召喚する光属性モンスターを選択',
        filter: (card: CardWithId) => card.type === 'monster' && card.attribute === '光',
        callback: (selectedCards: CardWithId[]) => {
          // カードが選択されなかった場合
          if (!selectedCards || selectedCards.length === 0) {
            addLog('輝聖女ルミナス: 対象となる光属性モンスターがいません', 'info');
            return;
          }
          const selectedCard = selectedCards[0];
          // フィールドの空きスロットを再確認
          const targetSlot = myField.findIndex(slot => slot === null);
          if (targetSlot === -1) {
            addLog('フィールドに空きがありません', 'info');
            return;
          }

          // モンスターインスタンスを作成
          const monsterInstance = createMonsterInstance(selectedCard);
          monsterInstance.canAttack = !summoningSickness; // 召喚酔い設定に依存
          monsterInstance.owner = currentPlayer;

          // 手札から削除
          setMyHand(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

          // フィールドに配置
          setMyField(prev => {
            const newField = [...prev];
            newField[targetSlot] = monsterInstance;
            return newField;
          });

          // トリガーを登録
          registerCardTriggers(monsterInstance, currentPlayer, targetSlot);

          // 常時効果を登録
          continuousEffectEngine.register(monsterInstance, currentPlayer);

          addLog(`輝聖女ルミナスの上級技: 「${selectedCard.name}」をコストなしで特殊召喚！`, 'heal');
        },
        selectionOwner: context.currentPlayer,
      });

      return true;
    }

    return false;
  },

  /**
   * C0000057: プリズム・ガーディアン
   * 基本技: 自分の光属性モンスター1体にこのターン【守護】（次に受けるダメージを半減）を付与。
   */
  C0000057: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 自分の光属性モンスターを取得
      const lightMonsters = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null && monster.attribute === '光');

      if (lightMonsters.length === 0) {
        addLog('プリズム・ガーディアン: 場に光属性モンスターがいません', 'info');
        return false;
      }

      const applyGuard = (targetIndex: number) => {
        const targetMonster = myField[targetIndex];
        if (!targetMonster) return;

        // GUARDステータスを付与（次に受けるダメージを半減、1回使用で消える）
        applyStatusToOwnMonster(context, targetIndex, STATUS_EFFECT_TYPES.GUARD, {
          duration: 0, // ターン終了時まで
          usesRemaining: 1, // 1回使用で消える
        }, 'プリズム・ガーディアン');

        addLog(`プリズム・ガーディアン: ${targetMonster.name}に【守護】を付与！`, 'heal');
      };

      // 1体のみの場合は自動選択
      if (lightMonsters.length === 1) {
        applyGuard(lightMonsters[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '【守護】を付与する光属性モンスターを選択',
          targetPlayer: 'self',
          validIndices: lightMonsters.map(l => l.index),
          callback: (selectedIndex: number) => {
            applyGuard(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初のモンスター
      applyGuard(lightMonsters[0].index);
      return true;
    }
    return false;
  },

  /**
   * C0000058: 熾天使セラフ
   * 基本技: 自分の墓地の光属性モンスター1体を手札に戻す。
   */
  C0000058: (skillText, context) => {
    const { addLog, setPendingGraveyardSelection, setShowGraveyardViewer } = context;
    const { myGraveyard, setMyGraveyard, setMyHand, currentPlayer } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 墓地の光属性モンスターを検索
      const lightMonsters = myGraveyard.filter(card =>
        card.type === 'monster' && card.attribute === '光'
      );

      if (lightMonsters.length === 0) {
        addLog('熾天使セラフ: 墓地に光属性モンスターがいません', 'info');
        return false;
      }

      // 手札に戻す処理
      const returnToHand = (targetCard: CardWithId) => {
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));
        setMyHand(prev => [...prev, targetCard]);
        addLog(`熾天使セラフ: ${targetCard.name}を墓地から手札に戻した`, 'heal');
      };

      // 1体のみの場合は自動選択
      if (lightMonsters.length === 1) {
        returnToHand(lightMonsters[0]);
        return true;
      }

      // 複数いる場合は墓地選択UIを表示
      if (setPendingGraveyardSelection && setShowGraveyardViewer) {
        setShowGraveyardViewer(currentPlayer);
        setPendingGraveyardSelection({
          message: '手札に戻す光属性モンスターを選択',
          filter: (card: CardWithId) => card.type === 'monster' && card.attribute === '光',
          callback: (selectedCards: CardWithId[] | CardWithId | null) => {
            const targetCard = Array.isArray(selectedCards) ? selectedCards[0] : selectedCards;
            if (targetCard) {
              returnToHand(targetCard);
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初の1体を選択
      returnToHand(lightMonsters[0]);
      return true;
    }
    return false;
  },

  /**
   * C0000070: 支配光の連鎖
   * 自分の場に光属性モンスターがいる場合、相手の手札を1枚ランダムに捨てる。
   */
  C0000070: (skillText, context) => {
    const { addLog } = context;
    const { myField, opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

    // 魔法カードなのでskillTextで判定しない
    // 場に光属性モンスターがいるか確認
    const hasLightMonster = myField.some(m => m && m.attribute === '光');

    if (!hasLightMonster) {
      addLog('場に光属性モンスターがいないため効果なし', 'info');
      return false;
    }

    if (opponentHand.length === 0) {
      addLog('相手の手札がありません', 'info');
      return true; // 条件は満たしていたので成功扱い
    }

    // ランダムに1枚選択して捨てる
    const randomIndex = Math.floor(Math.random() * opponentHand.length);
    const discardedCard = opponentHand[randomIndex];

    setOpponentHand(prev => prev.filter((_, idx) => idx !== randomIndex));
    setOpponentGraveyard(prev => [...prev, discardedCard]);

    addLog(`支配光の連鎖: 相手の手札「${discardedCard.name}」を捨てさせた！`, 'damage');
    return true;
  },

  /**
   * C0000064: ルミナスの裁き
   * 光以外の相手モンスター1体を破壊する。自分の場に光属性モンスターが2体以上いる場合自分はその攻撃力の半分回復する。
   */
  C0000064: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 光以外の相手モンスターを検索
    const nonLightMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null && monster.attribute !== '光');

    if (nonLightMonsters.length === 0) {
      addLog('ルミナスの裁き: 光以外の相手モンスターがいません', 'info');
      return false;
    }

    const executeDestruction = (targetIndex: number) => {
      const targetMonster = opponentField[targetIndex];
      if (!targetMonster) return;

      // モンスターを破壊
      setOpponentField((prev) => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });
      setOpponentGraveyard((prev) => [...prev, targetMonster]);
      addLog(`ルミナスの裁き: ${targetMonster.name}を破壊した！`, 'damage');

      // 光属性モンスターが2体以上いる場合、攻撃力の半分を回復
      const lightCount = myField.filter((m) => m && m.attribute === '光').length;
      if (lightCount >= 2) {
        const healAmount = Math.floor(targetMonster.currentAttack / 2);
        healLife(context, healAmount, true);
        addLog(`ルミナスの裁き: ${healAmount}ライフ回復！`, 'heal');
      }
    };

    // 1体のみの場合は自動選択
    if (nonLightMonsters.length === 1) {
      executeDestruction(nonLightMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '破壊する光以外のモンスターを選択',
        targetPlayer: 'opponent',
        validIndices: nonLightMonsters.map((m) => m.index),
        callback: (selectedIndex: number) => {
          executeDestruction(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 最初のモンスター
    executeDestruction(nonLightMonsters[0].index);
    return true;
  },

  /**
   * C0000065: 天使の波動
   * 相手モンスター全体に、場にいる光属性モンスターの数×300ダメージを与える。
   */
  C0000065: (skillText, context) => {
    const { addLog } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 場の光属性モンスターをカウント
    const lightCount = myField.filter((m) => m && m.attribute === '光').length;

    if (lightCount === 0) {
      addLog('天使の波動: 場に光属性モンスターがいないため効果なし', 'info');
      return false;
    }

    const damage = lightCount * 300;

    // 相手モンスター全体にダメージ
    const destroyedMonsters: MonsterInstance[] = [];
    setOpponentField((prev) => {
      return prev.map((monster) => {
        if (monster) {
          const newHp = monster.currentHp - damage;
          if (newHp <= 0) {
            destroyedMonsters.push(monster);
            return null;
          }
          return { ...monster, currentHp: newHp };
        }
        return monster;
      });
    });

    // 破壊されたモンスターを墓地に
    if (destroyedMonsters.length > 0) {
      setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters]);
    }

    addLog(`天使の波動: 相手モンスター全体に${damage}ダメージ！`, 'damage');
    return true;
  },

  /**
   * C0000066: ホーリーライトサモン
   * デッキからランダムなコスト3以下の光属性モンスター1体を場に出す（コスト不要）。
   */
  C0000066: (skillText, context) => {
    const { addLog, currentPlayer, summoningSickness = true } = context;
    const { myDeck, myField, setMyDeck, setMyField } = getPlayerContext(context);

    // デッキからコスト3以下の光属性モンスターを探す
    const candidates = myDeck.filter((card) =>
      card.type === 'monster' &&
      card.attribute === '光' &&
      card.cost <= 3
    );

    if (candidates.length === 0) {
      addLog('ホーリーライトサモン: デッキにコスト3以下の光属性モンスターがいません', 'info');
      return false;
    }

    // 空きスロットを探す
    const emptySlot = myField.findIndex((slot) => slot === null);
    if (emptySlot === -1) {
      addLog('ホーリーライトサモン: フィールドに空きがありません', 'info');
      return false;
    }

    // ランダムに1体選択
    const selectedCard = candidates[Math.floor(Math.random() * candidates.length)];

    // デッキから削除
    setMyDeck((prev) => prev.filter((c) => c.uniqueId !== selectedCard.uniqueId));

    // モンスターインスタンスを作成してフィールドに配置
    const monsterInstance = createMonsterInstance(selectedCard);
    monsterInstance.canAttack = !summoningSickness; // 召喚酔い設定に依存
    monsterInstance.owner = currentPlayer;
    setMyField((prev) => {
      const newField = [...prev];
      newField[emptySlot] = monsterInstance;
      return newField;
    });

    // トリガーと常時効果を登録
    registerCardTriggers(monsterInstance, currentPlayer, emptySlot);
    continuousEffectEngine.register(monsterInstance, currentPlayer);

    addLog(`ホーリーライトサモン: デッキから「${selectedCard.name}」を特殊召喚！`, 'heal');
    return true;
  },

  /**
   * C0000158: 聖光の刃
   * 相手のフィールドカード1枚を破壊する。場に光属性モンスターが1体以上いる場合、レスト状態のSPトークンを１つアクティブにする。
   */
  C0000158: (skillText, context) => {
    const { addLog } = context;
    const { myField, myRestedSP, setMyRestedSP, setMyActiveSP, opponentFieldCard, setOpponentFieldCard, setOpponentGraveyard } = getPlayerContext(context);

    // 相手のフィールドカードを破壊
    if (opponentFieldCard) {
      setOpponentGraveyard(prev => [...prev, opponentFieldCard]);
      setOpponentFieldCard(null);
      addLog(`聖光の刃: 相手のフィールドカード「${opponentFieldCard.name}」を破壊！`, 'damage');
    } else {
      addLog('聖光の刃: 相手のフィールドカードがありません', 'info');
    }

    // 場に光属性モンスターが1体以上いる場合、レストSPをアクティブに
    const hasLightMonster = myField.some(m => m && m.attribute === '光');
    if (hasLightMonster && myRestedSP > 0) {
      setMyRestedSP(prev => prev - 1);
      setMyActiveSP(prev => prev + 1);
      addLog('聖光の刃: 光属性モンスターがいるため、レストSPを1つアクティブにした！', 'info');
    }

    // フィールドカードがなくても効果自体は成功扱い
    return true;
  },

  /**
   * C0000067: ルミナス・ドミネーション
   * 相手の場にいるモンスター1体のコントロールをターン終了時まで奪う。そのモンスターの攻撃力は半分になる。
   */
  C0000067: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setMyField, setOpponentField } = getPlayerContext(context);

    // 相手モンスターを検索
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('ルミナス・ドミネーション: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // 空きスロットを探す
    const emptySlot = myField.findIndex((slot) => slot === null);
    if (emptySlot === -1) {
      addLog('ルミナス・ドミネーション: 自分のフィールドに空きがありません', 'info');
      return false;
    }

    const executeControl = (targetIndex: number) => {
      const targetMonster = opponentField[targetIndex];
      if (!targetMonster) return;

      // 相手フィールドから削除
      setOpponentField((prev) => {
        const newField = [...prev];
        newField[targetIndex] = null;
        return newField;
      });

      // 攻撃力半減して自分のフィールドに配置
      const controlledMonster = {
        ...targetMonster,
        currentAttack: Math.floor(targetMonster.currentAttack / 2),
        canAttack: true,
        controlledUntilEndTurn: true, // ターン終了時に戻すフラグ
        originalOwner: targetMonster.owner,
        originalSlot: targetIndex,
      };

      setMyField((prev) => {
        const newField = [...prev];
        newField[emptySlot] = controlledMonster;
        return newField;
      });

      addLog(`ルミナス・ドミネーション: ${targetMonster.name}のコントロールを奪った！（攻撃力半分）`, 'info');
    };

    // 1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      executeControl(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'コントロールを奪うモンスターを選択',
        targetPlayer: 'opponent',
        validIndices: opponentMonsters.map((m) => m.index),
        callback: (selectedIndex: number) => {
          executeControl(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック: 最初のモンスター
    executeControl(opponentMonsters[0].index);
    return true;
  },

  /**
   * C0000068: クリスタルブレス
   * 次の自分のターン、自分の光属性モンスターの召喚コストを1軽減。
   */
  C0000068: (skillText, context) => {
    const { addLog, currentPlayer } = context;
    const { myStatusEffects } = getPlayerContext(context);

    // プレイヤーバフとして光属性召喚コスト軽減を付与
    const result = statusEffectEngine.applyPlayerStatus(
      myStatusEffects,
      STATUS_EFFECT_TYPES.LIGHT_SUMMON_COST_REDUCTION,
      {
        source: 'C0000068',
        sourceName: 'クリスタルブレス',
        value: 1,       // 軽減量
        duration: 1,    // 次の自分のターン終了時まで
      }
    );

    if (result.success) {
      addLog('クリスタルブレス: 次の自分のターン、光属性モンスターの召喚コストを1軽減！', 'info');
      return true;
    }

    addLog('クリスタルブレス: 効果の付与に失敗しました', 'info');
    return false;
  },

  /**
   * C0000069: 聖なる浄化
   * 場にいる全てのモンスターの効果をターン終了時まで無効化し、場のモンスター全体に2000ダメージを与える。
   */
  C0000069: (skillText, context) => {
    const { addLog } = context;
    const { myField, opponentField, setMyField, setOpponentField, setMyGraveyard, setOpponentGraveyard } = getPlayerContext(context);

    const damage = 2000;
    const myDestroyedMonsters: MonsterInstance[] = [];
    const opponentDestroyedMonsters: MonsterInstance[] = [];

    // 自分のモンスターにダメージ＆SILENCE付与
    setMyField((prev) => {
      return prev.map((monster) => {
        if (monster) {
          const newHp = monster.currentHp - damage;
          if (newHp <= 0) {
            myDestroyedMonsters.push(monster);
            return null;
          }
          // SILENCE状態異常を付与
          statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.SILENCE, {
            duration: 0, // このターン終了時まで
            source: 'C0000069',
            sourceName: '聖なる浄化',
          });
          return {
            ...monster,
            currentHp: newHp,
          };
        }
        return monster;
      });
    });

    // 相手のモンスターにダメージ＆SILENCE付与
    setOpponentField((prev) => {
      return prev.map((monster) => {
        if (monster) {
          const newHp = monster.currentHp - damage;
          if (newHp <= 0) {
            opponentDestroyedMonsters.push(monster);
            return null;
          }
          // SILENCE状態異常を付与
          statusEffectEngine.applyStatus(monster, STATUS_EFFECT_TYPES.SILENCE, {
            duration: 0, // このターン終了時まで
            source: 'C0000069',
            sourceName: '聖なる浄化',
          });
          return {
            ...monster,
            currentHp: newHp,
          };
        }
        return monster;
      });
    });

    // 破壊されたモンスターを墓地に
    if (myDestroyedMonsters.length > 0) {
      setMyGraveyard((prev) => [...prev, ...myDestroyedMonsters]);
    }
    if (opponentDestroyedMonsters.length > 0) {
      setOpponentGraveyard((prev) => [...prev, ...opponentDestroyedMonsters]);
    }

    const totalDestroyed = myDestroyedMonsters.length + opponentDestroyedMonsters.length;
    addLog(`聖なる浄化: 場のモンスター全体に2000ダメージ！効果無効化！（${totalDestroyed}体破壊）`, 'damage');
    return true;
  },

  // ========================================
  // ヴォランティス・鳥民シリーズ
  // ========================================

  /**
   * C0000345: ヴォランティス・アルディオン
   * 基本技: 相手モンスター1体に800ダメージを与え、次のターン終了時まで行動不能にする。
   * 上級技: 手札を1枚捨て、相手フィールド全体に1000ダメージ。
   */
  C0000345: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, setPendingHandSelection } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard, myHand, setMyHand, setMyGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 相手モンスターを検索
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('ヴォランティス・アルディオンの基本技: 相手モンスターがいません', 'info');
        return false;
      }

      const applyEffect = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          if (newField[targetIndex]) {
            const target = newField[targetIndex];
            const newHp = target.currentHp - 800;
            if (newHp <= 0) {
              setOpponentGraveyard(p => [...p, target]);
              addLog(`ヴォランティス・アルディオンの基本技: ${target.name}に800ダメージで破壊！`, 'damage');
              newField[targetIndex] = null;
            } else {
              newField[targetIndex] = {
                ...target,
                currentHp: newHp,
                actionDisabled: true, // 行動不能
                actionDisabledUntilTurn: 2, // 次のターン終了時まで
              };
              addLog(`ヴォランティス・アルディオンの基本技: ${target.name}に800ダメージ、行動不能に！`, 'damage');
            }
          }
          return newField;
        });
      };

      if (opponentMonsters.length === 1) {
        applyEffect(opponentMonsters[0].index);
        return true;
      }

      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '800ダメージを与えるモンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyEffect(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyEffect(opponentMonsters[0].index);
      return true;
    }

    if (context.skillType === 'advanced') {
      if (myHand.length === 0) {
        addLog('ヴォランティス・アルディオンの上級技: 手札がありません', 'info');
        return false;
      }

      const executeAdvanced = (discardCard: CardWithId) => {
        // 手札を捨てる
        setMyHand(prev => prev.filter(c => c.uniqueId !== discardCard.uniqueId));
        setMyGraveyard(prev => [...prev, discardCard]);
        addLog(`${discardCard.name}を捨てた`, 'info');

        // 相手フィールド全体に1000ダメージ
        const destroyedMonsters: MonsterInstance[] = [];
        setOpponentField(prev => {
          return prev.map(monster => {
            if (monster) {
              const newHp = monster.currentHp - 1000;
              if (newHp <= 0) {
                destroyedMonsters.push(monster);
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        if (destroyedMonsters.length > 0) {
          setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
        }

        addLog(`ヴォランティス・アルディオンの上級技: 相手全体に1000ダメージ！（${destroyedMonsters.length}体破壊）`, 'damage');
      };

      if (myHand.length === 1) {
        executeAdvanced(myHand[0]);
        return true;
      }

      if (setPendingHandSelection) {
        setPendingHandSelection({
          message: '捨てる手札を選択',
          callback: (selectedCards: CardWithId[]) => {
            if (selectedCards && selectedCards.length > 0) {
              executeAdvanced(selectedCards[0]);
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      executeAdvanced(myHand[0]);
      return true;
    }

    return false;
  },

  /**
   * C0000346: ヴォランティス・セラヴェント
   * 基本技: このカードの攻撃力をターン終了時まで1000アップ。
   */
  C0000346: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (monsterIndex === undefined || !myField[monsterIndex]) {
        addLog('ヴォランティス・セラヴェントの基本技: 対象が見つかりません', 'info');
        return false;
      }

      setMyField(prev => {
        const newField = [...prev];
        const monster = newField[monsterIndex];
        if (monster) {
          newField[monsterIndex] = {
            ...monster,
            currentAttack: (monster.currentAttack ?? monster.attack ?? 0) + 1000,
          };
        }
        return newField;
      });

      addLog('ヴォランティス・セラヴェントの基本技: 攻撃力+1000！', 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000347: 鳥民の供物者・カルディス
   * 基本技: このカードをリリースし、次の《ヴォランティス》モンスターの召喚コストを2軽減。
   */
  C0000347: (skillText, context) => {
    const { addLog, monsterIndex, currentPlayer } = context;
    const { myField, setMyField, setMyGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (monsterIndex === undefined || !myField[monsterIndex]) {
        addLog('鳥民の供物者・カルディスの基本技: 対象が見つかりません', 'info');
        return false;
      }

      const monster = myField[monsterIndex];

      // カードをリリースして墓地に送る
      setMyField(prev => {
        const newField = [...prev];
        newField[monsterIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, monster]);

      // コスト軽減フラグをセット（グローバルコンテキストで管理）
      // 次のヴォランティス召喚時にコスト-2
      if (context.setVolantisCostReduction) {
        context.setVolantisCostReduction((prev: number) => (prev || 0) + 2);
      }

      addLog('鳥民の供物者・カルディスの基本技: リリースして次のヴォランティス召喚コスト-2！', 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000348: ヴォランティス・ファルクェス
   * 基本技: 相手モンスター1体の効果をターン終了時まで無効化。
   */
  C0000348: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('ヴォランティス・ファルクェスの基本技: 相手モンスターがいません', 'info');
        return false;
      }

      const applyNegate = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          if (newField[targetIndex]) {
            const target = newField[targetIndex];
            // SILENCE状態異常を付与
            statusEffectEngine.applyStatus(target, STATUS_EFFECT_TYPES.SILENCE, {
              duration: 0, // このターン終了時まで
              source: 'C0000348',
              sourceName: 'ヴォランティス・ファルクェス',
            });
            newField[targetIndex] = { ...target };
            addLog(`ヴォランティス・ファルクェスの基本技: ${target.name}の効果を無効化！`, 'info');
          }
          return newField;
        });
      };

      if (opponentMonsters.length === 1) {
        applyNegate(opponentMonsters[0].index);
        return true;
      }

      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '効果を無効化するモンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyNegate(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyNegate(opponentMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000354: 鳥民の風使い・ゼルヴィス
   * 基本技: 自分のモンスター1体のHPを800回復。場に《ヴォランティス》モンスターがいる場合、このカードの攻撃力をターン終了時まで800アップ。
   */
  C0000354: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const myMonsters = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (myMonsters.length === 0) {
        addLog('鳥民の風使い・ゼルヴィスの基本技: 対象モンスターがいません', 'info');
        return false;
      }

      const applyHeal = (targetIndex: number) => {
        setMyField(prev => {
          const newField = [...prev];
          if (newField[targetIndex]) {
            const target = newField[targetIndex];
            const maxHp = target.maxHp ?? target.hp ?? target.currentHp;
            const healAmount = Math.min(800, maxHp - target.currentHp);
            newField[targetIndex] = {
              ...target,
              currentHp: target.currentHp + healAmount,
            };
            addLog(`鳥民の風使い・ゼルヴィスの基本技: ${target.name}のHPを${healAmount}回復！`, 'heal');
          }
          return newField;
        });

        // 《ヴォランティス》がいるか確認
        const hasVolantis = myField.some(m => m && m.name && m.name.includes('ヴォランティス'));
        if (hasVolantis && monsterIndex !== undefined && myField[monsterIndex]) {
          setMyField(prev => {
            const newField = [...prev];
            const monster = newField[monsterIndex];
            if (monster) {
              newField[monsterIndex] = {
                ...monster,
                currentAttack: (monster.currentAttack ?? monster.attack ?? 0) + 800,
              };
            }
            return newField;
          });
          addLog('ヴォランティスがいるため、ゼルヴィスの攻撃力+800！', 'info');
        }
      };

      if (myMonsters.length === 1) {
        applyHeal(myMonsters[0].index);
        return true;
      }

      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: 'HP800回復するモンスターを選択',
          targetPlayer: 'self',
          callback: (selectedIndex: number) => {
            applyHeal(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyHeal(myMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000356: 鳥民の斥候・ヴェルティス
   * 基本技: このカードをリリースし、次の自分のターン開始時まで、自分の《ヴォランティス》モンスターが受ける効果ダメージを0にする。
   */
  C0000356: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField, setMyGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (monsterIndex === undefined || !myField[monsterIndex]) {
        addLog('鳥民の斥候・ヴェルティスの基本技: 対象が見つかりません', 'info');
        return false;
      }

      const monster = myField[monsterIndex];

      // カードをリリースして墓地に送る
      setMyField(prev => {
        const newField = [...prev];
        newField[monsterIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, monster]);

      // 効果ダメージ無効化フラグをヴォランティスに付与
      setMyField(prev => {
        return prev.map(m => {
          if (m && m.name && m.name.includes('ヴォランティス')) {
            return { ...m, effectDamageImmune: true };
          }
          return m;
        });
      });

      addLog('鳥民の斥候・ヴェルティスの基本技: リリースしてヴォランティスに効果ダメージ無効を付与！', 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000357: 風刻の使者・シルヴァリス
   * 基本技: 相手モンスター1体の攻撃力をターン終了時まで400下げる。
   */
  C0000357: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('風刻の使者・シルヴァリスの基本技: 相手モンスターがいません', 'info');
        return false;
      }

      const applyDebuff = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          if (newField[targetIndex]) {
            const target = newField[targetIndex];
            const newAtk = Math.max(0, (target.currentAttack ?? target.attack ?? 0) - 400);
            newField[targetIndex] = {
              ...target,
              currentAttack: newAtk,
            };
            addLog(`風刻の使者・シルヴァリスの基本技: ${target.name}の攻撃力-400！`, 'info');
          }
          return newField;
        });
      };

      if (opponentMonsters.length === 1) {
        applyDebuff(opponentMonsters[0].index);
        return true;
      }

      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '攻撃力を下げるモンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            applyDebuff(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      applyDebuff(opponentMonsters[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000368: 裁定の鳥民・ミナルス
   * 基本技: このカードをリリースし、自分の墓地の《ヴォランティス》モンスター1体を手札に戻す。そのターン終了時まで、自分の《ヴォランティス》モンスターが受ける戦闘ダメージを半減。
   */
  C0000368: (skillText, context) => {
    const { addLog, monsterIndex, setPendingGraveyardSelection, setShowGraveyardViewer } = context;
    const { myField, setMyField, myGraveyard, setMyGraveyard, setMyHand, currentPlayer } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      if (monsterIndex === undefined || !myField[monsterIndex]) {
        addLog('裁定の鳥民・ミナルスの基本技: 対象が見つかりません', 'info');
        return false;
      }

      // 墓地の《ヴォランティス》モンスターを確認
      const volantisInGraveyard = myGraveyard.filter(c =>
        c.type === 'monster' && c.name && c.name.includes('ヴォランティス')
      );

      if (volantisInGraveyard.length === 0) {
        addLog('裁定の鳥民・ミナルスの基本技: 墓地に《ヴォランティス》モンスターがいません', 'info');
        return false;
      }

      const monster = myField[monsterIndex];

      const executeEffect = (selectedCard: CardWithId) => {
        // カードをリリースして墓地に送る
        setMyField(prev => {
          const newField = [...prev];
          newField[monsterIndex] = null;
          return newField;
        });
        setMyGraveyard(prev => [...prev, monster]);

        // 墓地から手札に戻す
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
        setMyHand(prev => [...prev, selectedCard]);
        addLog(`裁定の鳥民・ミナルスの基本技: 「${selectedCard.name}」を手札に戻した`, 'info');

        // 戦闘ダメージ半減フラグをヴォランティスに付与
        setMyField(prev => {
          return prev.map(m => {
            if (m && m.name && m.name.includes('ヴォランティス')) {
              return { ...m, battleDamageHalved: true };
            }
            return m;
          });
        });
        addLog('ヴォランティスの戦闘ダメージが半減！', 'info');
      };

      if (volantisInGraveyard.length === 1) {
        executeEffect(volantisInGraveyard[0]);
        return true;
      }

      // 墓地選択UI
      if (setPendingGraveyardSelection && setShowGraveyardViewer) {
        setShowGraveyardViewer(currentPlayer);
        setPendingGraveyardSelection({
          message: '手札に戻す《ヴォランティス》モンスターを選択',
          filter: (card: CardWithId) => card.type === 'monster' && !!card.name && card.name.includes('ヴォランティス'),
          callback: (selectedCards: CardWithId[] | CardWithId | null) => {
            const targetCard = Array.isArray(selectedCards) ? selectedCards[0] : selectedCards;
            if (targetCard) {
              executeEffect(targetCard);
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      executeEffect(volantisInGraveyard[0]);
      return true;
    }

    return false;
  },

  /**
   * C0000349: アヴィクルスの裁定（魔法カード）
   * 相手モンスター1体の攻撃力を1000下げ、次のターン終了時まで行動不能にする。
   * 場に《ヴォランティス》モンスターがいる場合、相手プレイヤーに500ダメージ。
   */
  C0000349: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentLife } = getPlayerContext(context);

    // 相手モンスターを検索
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('アヴィクルスの裁定: 相手モンスターがいません', 'info');
      return false;
    }

    const applyEffect = (targetIndex: number) => {
      setOpponentField(prev => {
        const newField = [...prev];
        if (newField[targetIndex]) {
          const target = newField[targetIndex];
          const newAtk = Math.max(0, (target.currentAttack ?? target.attack ?? 0) - 1000);
          newField[targetIndex] = {
            ...target,
            currentAttack: newAtk,
            actionDisabled: true,
            actionDisabledUntilTurn: 2,
          };
          addLog(`アヴィクルスの裁定: ${target.name}の攻撃力-1000、行動不能に！`, 'damage');
        }
        return newField;
      });

      // 《ヴォランティス》がいるか確認
      const hasVolantis = myField.some(m => m && m.name && m.name.includes('ヴォランティス'));
      if (hasVolantis) {
        conditionalDamage(context, 500, 'opponent');
        addLog('ヴォランティスがいるため、相手に500ダメージ！', 'damage');
      }
    };

    if (opponentMonsters.length === 1) {
      applyEffect(opponentMonsters[0].index);
      return true;
    }

    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力を下げて行動不能にするモンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    applyEffect(opponentMonsters[0].index);
    return true;
  },

  /**
   * C0000355: 鳥民の壮麗儀式（魔法カード）
   * 自分の手札から《ヴォランティス》モンスター1枚を墓地に送り、デッキから《ヴォランティス》モンスター1枚を手札に加える。
   */
  C0000355: (skillText, context) => {
    const { addLog, setPendingHandSelection } = context;
    const { myHand, setMyHand, myDeck, setMyDeck, setMyGraveyard } = getPlayerContext(context);

    // 手札に《ヴォランティス》モンスターがあるか確認
    const volantisInHand = myHand.filter(c =>
      c.type === 'monster' && c.name && c.name.includes('ヴォランティス')
    );

    if (volantisInHand.length === 0) {
      addLog('鳥民の壮麗儀式: 手札に《ヴォランティス》モンスターがありません', 'info');
      return false;
    }

    const executeEffect = (discardCard: CardWithId) => {
      // 手札から墓地に送る
      setMyHand(prev => prev.filter(c => c.uniqueId !== discardCard.uniqueId));
      setMyGraveyard(prev => [...prev, discardCard]);
      addLog(`${discardCard.name}を墓地に送った`, 'info');

      // デッキから《ヴォランティス》モンスターをサーチ
      const found = searchCard(context, (card) =>
        card.type === 'monster' && !!card.name && card.name.includes('ヴォランティス')
      );

      if (!found) {
        addLog('鳥民の壮麗儀式: デッキに《ヴォランティス》モンスターがありません', 'info');
      }
    };

    if (volantisInHand.length === 1) {
      executeEffect(volantisInHand[0]);
      return true;
    }

    if (setPendingHandSelection) {
      setPendingHandSelection({
        message: '墓地に送る《ヴォランティス》モンスターを選択',
        filter: (card: CardWithId) => card.type === 'monster' && !!card.name && card.name.includes('ヴォランティス'),
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            executeEffect(selectedCards[0]);
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    executeEffect(volantisInHand[0]);
    return true;
  },

  /**
   * C0000358: アヴィクルスの風鳴り（魔法カード）
   * 自分のデッキの上から4枚を見て、《ヴォランティス》カード1枚を手札に加え、残りを墓地に送る。
   * 場に《ヴォランティス》モンスターがいる場合、このターン中、自分の《ヴォランティス》モンスターの攻撃力を300アップ。
   */
  C0000358: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, setMyHand, setMyGraveyard, myField, setMyField } = getPlayerContext(context);

    if (myDeck.length === 0) {
      addLog('アヴィクルスの風鳴り: デッキがありません', 'info');
      return false;
    }

    // デッキの上から4枚を取得
    const topCards = myDeck.slice(0, Math.min(4, myDeck.length));
    const remainingDeck = myDeck.slice(Math.min(4, myDeck.length));

    // 《ヴォランティス》カードがあるかチェック
    const volantisCards = topCards.filter(c => c.name && c.name.includes('ヴォランティス'));

    const applyAtkBuff = () => {
      // 《ヴォランティス》がいるか確認
      const hasVolantis = myField.some(m => m && m.name && m.name.includes('ヴォランティス'));
      if (hasVolantis) {
        setMyField(prev => {
          return prev.map(m => {
            if (m && m.name && m.name.includes('ヴォランティス')) {
              return {
                ...m,
                currentAttack: (m.currentAttack ?? m.attack ?? 0) + 300,
              };
            }
            return m;
          });
        });
        addLog('ヴォランティスがいるため、ヴォランティス全体の攻撃力+300！', 'info');
      }
    };

    if (volantisCards.length === 0) {
      // ヴォランティスがない場合、全て墓地に送る
      setMyGraveyard(prev => [...prev, ...topCards]);
      setMyDeck(remainingDeck);
      addLog('アヴィクルスの風鳴り: 《ヴォランティス》カードがなく、4枚を墓地に送った', 'info');
      applyAtkBuff();
      return true;
    }

    // 1枚のみの場合は自動選択
    if (volantisCards.length === 1) {
      const selectedCard = volantisCards[0];
      const otherCards = topCards.filter(c => c.uniqueId !== selectedCard.uniqueId);
      setMyHand(prev => [...prev, selectedCard]);
      setMyGraveyard(prev => [...prev, ...otherCards]);
      setMyDeck(remainingDeck);
      addLog(`アヴィクルスの風鳴り: 「${selectedCard.name}」を手札に加え、残りを墓地へ`, 'info');
      applyAtkBuff();
      return true;
    }

    // 複数ある場合はデッキ確認UIを使用
    if (setPendingDeckReview) {
      setPendingDeckReview({
        cards: topCards,
        message: '手札に加える《ヴォランティス》カードを選択',
        filter: (card: CardWithId) => !!card.name && card.name.includes('ヴォランティス'),
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            const selectedCard = selectedCards[0];
            const otherCards = topCards.filter(c => c.uniqueId !== selectedCard.uniqueId);
            setMyHand(prev => [...prev, selectedCard]);
            setMyGraveyard(prev => [...prev, ...otherCards]);
            setMyDeck(remainingDeck);
            addLog(`アヴィクルスの風鳴り: 「${selectedCard.name}」を手札に加え、残りを墓地へ`, 'info');
            applyAtkBuff();
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    const selectedCard = volantisCards[0];
    const otherCards = topCards.filter(c => c.uniqueId !== selectedCard.uniqueId);
    setMyHand(prev => [...prev, selectedCard]);
    setMyGraveyard(prev => [...prev, ...otherCards]);
    setMyDeck(remainingDeck);
    addLog(`アヴィクルスの風鳴り: 「${selectedCard.name}」を手札に加え、残りを墓地へ`, 'info');
    applyAtkBuff();
    return true;
  },

  /**
   * C0000216: フルーツの糸引き（魔法カード）
   * 場に《フルーツ・マリオネット》モンスターがいるとき、そのターン中、
   * 禁忌カード以外の相手モンスター1体のコントロールを奪う（そのモンスターは攻撃できない）。
   * このカードを発動したターン、自分のライフを400回復。
   */
  C0000216: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, setMyLife } = getPlayerContext(context);

    // 場に《フルーツ・マリオネット》モンスターがいるか確認
    const hasFruitMarionette = myField.some(m =>
      m && m.name && m.name.includes('フルーツ・マリオネット')
    );

    if (!hasFruitMarionette) {
      addLog('フルーツの糸引き: 場に《フルーツ・マリオネット》モンスターがいません', 'info');
      return false;
    }

    // 空きスロットを確認
    const emptySlotIndex = myField.findIndex(slot => slot === null);
    if (emptySlotIndex === -1) {
      addLog('フルーツの糸引き: 自分の場に空きがありません', 'info');
      return false;
    }

    // 相手の場に禁忌カード以外のモンスターがいるか
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && !monster.forbidden);

    if (validTargets.length === 0) {
      addLog('フルーツの糸引き: 奪えるモンスターがいません', 'info');
      return false;
    }

    // ライフ400回復
    setMyLife(prev => prev + 400);
    addLog('フルーツの糸引き: ライフを400回復', 'heal');

    // モンスター選択
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'コントロールを奪うモンスターを選択',
        targetPlayer: 'opponent',
        validTargets: validTargets.map(t => t.index),
        callback: (selectedIndex: number) => {
          const targetMonster = opponentField[selectedIndex];
          if (targetMonster) {
            // 相手の場から除去
            setOpponentField(prev => {
              const newField = [...prev];
              newField[selectedIndex] = null;
              return newField;
            });

            // 自分の場に移動（攻撃不可、ターン終了時に戻る）
            const stolenMonster: MonsterInstance = {
              ...targetMonster,
              controlStolen: true,
              originalOwner: (context.currentPlayer === 1 ? 2 : 1) as PlayerNumber,
              originalSlot: selectedIndex,
              controlReturnTurn: 0, // このターン終了時に戻る
              canAttack: false, // 攻撃不可
            };

            setMyField(prev => {
              const newField = [...prev];
              const slot = newField.findIndex(s => s === null);
              if (slot !== -1) {
                newField[slot] = stolenMonster;
              }
              return newField;
            });

            addLog(`フルーツの糸引き: ${targetMonster.name}のコントロールを奪った（攻撃不可）`, 'info');
          }
        },
        selectionOwner: context.currentPlayer,
      });
    }

    return true;
  },

  /**
   * C0000217: 光果の舞踏（魔法カード）【刹那詠唱】
   * 自分の場に光属性【プラント】モンスターがいるとき、自分の場のモンスター1体を破壊して、
   * そのコスト以下の光属性【プラント】モンスターを手札から召喚できる。
   */
  C0000217: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, setPendingHandSelection, summoningSickness = true } = context;
    const { myField, setMyField, myHand, setMyHand, myGraveyard, setMyGraveyard } = getPlayerContext(context);
    const { hasCategory } = require('../../utils/helpers');

    // 場に光属性【プラント】がいるか確認
    const hasLightPlant = myField.some(m =>
      m && m.attribute === '光' && hasCategory(m, 'プラント')
    );

    if (!hasLightPlant) {
      addLog('光果の舞踏: 場に光属性【プラント】モンスターがいません', 'info');
      return false;
    }

    // 破壊可能なモンスターを確認
    const destroyableMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (destroyableMonsters.length === 0) {
      addLog('光果の舞踏: 破壊するモンスターがいません', 'info');
      return false;
    }

    // モンスター選択（破壊対象）
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '破壊するモンスターを選択',
        targetPlayer: 'self',
        validTargets: destroyableMonsters.map(t => t.index),
        callback: (selectedIndex: number) => {
          const targetMonster = myField[selectedIndex];
          if (!targetMonster) return;

          const destroyedCost = targetMonster.cost || 0;

          // モンスターを破壊して墓地へ
          setMyField(prev => {
            const newField = [...prev];
            newField[selectedIndex] = null;
            return newField;
          });
          setMyGraveyard(prev => [...prev, targetMonster]);
          addLog(`光果の舞踏: ${targetMonster.name}を破壊`, 'info');

          // 手札からコスト以下の光属性【プラント】を選択
          const validHandCards = myHand.filter(c =>
            c.type === 'monster' &&
            c.attribute === '光' &&
            hasCategory(c, 'プラント') &&
            (c.cost || 0) <= destroyedCost
          );

          if (validHandCards.length === 0) {
            addLog('光果の舞踏: 召喚できる光属性【プラント】が手札にありません', 'info');
            return;
          }

          if (setPendingHandSelection) {
            setPendingHandSelection({
              message: `召喚する光属性【プラント】を選択（コスト${destroyedCost}以下）`,
              filter: (card: CardWithId) =>
                card.type === 'monster' &&
                card.attribute === '光' &&
                hasCategory(card, 'プラント') &&
                (card.cost || 0) <= destroyedCost,
              callback: (selectedCards: CardWithId[] | CardWithId | null) => {
                const targetCard = Array.isArray(selectedCards) ? selectedCards[0] : selectedCards;
                if (targetCard) {
                  // 手札から除去
                  setMyHand(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));

                  // 場に召喚
                  const newMonster: MonsterInstance = {
                    ...targetCard,
                    currentHp: targetCard.hp ?? 0,
                    currentAttack: targetCard.attack ?? 0,
                    canAttack: !summoningSickness, // 召喚酔い設定に依存
                    charges: [],
                    statusEffects: [],
                  };

                  setMyField(prev => {
                    const newField = [...prev];
                    newField[selectedIndex] = newMonster;
                    return newField;
                  });

                  addLog(`光果の舞踏: ${targetCard.name}を召喚！`, 'info');
                }
              },
              selectionOwner: context.currentPlayer,
            });
          }
        },
        selectionOwner: context.currentPlayer,
      });
    }

    return true;
  },

  /**
   * C0000236: プリティ☆プリンセス（魔法カード）
   * 場にいる《リリカ》モンスター1体の攻撃力をターン終了時まで1000アップし、
   * 相手モンスターに300ダメージを与える。
   */
  C0000236: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 場に《リリカ》モンスターがいるか確認
    const lilikaMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.name && monster.name.includes('リリカ'));

    if (lilikaMonsters.length === 0) {
      addLog('プリティ☆プリンセス: 場に《リリカ》モンスターがいません', 'info');
      return false;
    }

    // リリカが1体の場合は自動選択
    const applyEffect = (lilikaIndex: number) => {
      // 攻撃力1000アップ
      setMyField(prev => prev.map((m, idx) => {
        if (idx === lilikaIndex && m) {
          addLog(`プリティ☆プリンセス: ${m.name}の攻撃力+1000！`, 'info');
          return {
            ...m,
            currentAttack: (m.currentAttack ?? m.attack ?? 0) + 1000,
            atkBuffedUntilEndOfTurn: true,
          };
        }
        return m;
      }));

      // 相手モンスターに300ダメージ
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('プリティ☆プリンセス: 相手の場にモンスターがいません', 'info');
        return;
      }

      if (opponentMonsters.length === 1) {
        // 1体の場合は自動
        const target = opponentMonsters[0];
        const targetMonster = target.monster!;
        const newHp = (targetMonster.currentHp ?? targetMonster.hp ?? 0) - 300;
        if (newHp <= 0) {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[target.index] = null;
            return newField;
          });
          setOpponentGraveyard(prev => [...prev, targetMonster as unknown as CardWithId]);
          addLog(`プリティ☆プリンセス: ${targetMonster.name}に300ダメージ！→破壊`, 'damage');
        } else {
          setOpponentField(prev => prev.map((m, idx) => {
            if (idx === target.index && m) {
              return { ...m, currentHp: newHp };
            }
            return m;
          }));
          addLog(`プリティ☆プリンセス: ${targetMonster.name}に300ダメージ！`, 'damage');
        }
      } else if (setPendingMonsterTarget) {
        // 複数の場合は選択
        setPendingMonsterTarget({
          message: '300ダメージを与えるモンスターを選択',
          targetPlayer: 'opponent',
          callback: (selectedIndex: number) => {
            const targetMonster = opponentField[selectedIndex];
            if (targetMonster) {
              const newHp = (targetMonster.currentHp ?? targetMonster.hp ?? 0) - 300;
              if (newHp <= 0) {
                setOpponentField(prev => {
                  const newField = [...prev];
                  newField[selectedIndex] = null;
                  return newField;
                });
                setOpponentGraveyard(prev => [...prev, targetMonster]);
                addLog(`プリティ☆プリンセス: ${targetMonster.name}に300ダメージ！→破壊`, 'damage');
              } else {
                setOpponentField(prev => prev.map((m, idx) => {
                  if (idx === selectedIndex && m) {
                    return { ...m, currentHp: newHp };
                  }
                  return m;
                }));
                addLog(`プリティ☆プリンセス: ${targetMonster.name}に300ダメージ！`, 'damage');
              }
            }
          },
          selectionOwner: context.currentPlayer,
        });
      }
    };

    if (lilikaMonsters.length === 1) {
      applyEffect(lilikaMonsters[0].index);
    } else if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力をアップする《リリカ》を選択',
        targetPlayer: 'self',
        validTargets: lilikaMonsters.map(t => t.index),
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
    }

    return true;
  },

  // ========================================
  // フェイズカード
  // ========================================

  /**
   * C0000230: 永遠の灯の祈り（フェイズカード）
   * 3枚重ね: 自分の光属性モンスター全体のHPを1000アップし、相手フィールド全体に800ダメージを与える。
   * その後、このカードを墓地に送る。
   */
  C0000230: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (skillText.includes('3枚重ね') || skillText.includes('３枚重ね')) {
      // 自分の光属性モンスター全体のHP+1000
      let buffedCount = 0;
      setMyField(prev => {
        return prev.map(monster => {
          if (monster && monster.attribute === '光') {
            buffedCount++;
            return {
              ...monster,
              currentHp: (monster.currentHp ?? monster.hp ?? 0) + 1000,
              hp: (monster.hp ?? 0) + 1000,
            };
          }
          return monster;
        });
      });

      if (buffedCount > 0) {
        addLog(`永遠の灯の祈り【3枚重ね】: 光属性モンスター${buffedCount}体のHP+1000！`, 'heal');
      }

      // 相手フィールド全体に800ダメージ
      const destroyedMonsters: MonsterInstance[] = [];
      setOpponentField(prev => {
        return prev.map(monster => {
          if (monster) {
            const newHp = (monster.currentHp ?? monster.hp ?? 0) - 800;
            if (newHp <= 0) {
              destroyedMonsters.push(monster);
              return null;
            }
            return {
              ...monster,
              currentHp: newHp,
            };
          }
          return monster;
        });
      });

      // 破壊されたモンスターを墓地に送る
      if (destroyedMonsters.length > 0) {
        setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
      }

      addLog(`永遠の灯の祈り【3枚重ね】: 相手フィールド全体に800ダメージ！${destroyedMonsters.length > 0 ? `（${destroyedMonsters.length}体破壊）` : ''}`, 'damage');

      return true;
    }

    return false;
  },

  /**
   * C0000352: アヴィクルスの試練（フェイズカード）
   * 3枚重ね: 全ての相手モンスターを「行動不能」にし、相手の手札上限を2減らす。自分に300ダメージ。
   * Note: 手札上限減少は未実装（システム対応必要）
   */
  C0000352: (skillText, context) => {
    const { addLog } = context;
    const { setOpponentField, setMyLife } = getPlayerContext(context);

    // 3枚重ね効果の判定（段階効果テキストに「3枚重ね」が含まれる場合）
    if (skillText.includes('3枚重ね') || skillText.includes('３枚重ね')) {
      // 全ての相手モンスターを行動不能にする
      let disabledCount = 0;
      setOpponentField(prev => {
        return prev.map(monster => {
          if (monster) {
            disabledCount++;
            return {
              ...monster,
              actionDisabled: true,
              actionDisabledUntilTurn: 2, // 次のターン終了時まで
            };
          }
          return monster;
        });
      });

      if (disabledCount > 0) {
        addLog(`アヴィクルスの試練【最終段階】: 相手モンスター${disabledCount}体を行動不能に！`, 'damage');
      }

      // 手札上限-2は未実装（システム対応必要）
      addLog('※手札上限-2効果は未実装', 'info');

      // 自分に300ダメージ
      setMyLife(prev => prev - 300);
      addLog('アヴィクルスの試練【最終段階】: 自分に300ダメージ！', 'damage');

      return true;
    }

    return false;
  },

  // ========================================
  // 【ポイズンウェイル】関連カード
  // ========================================

  /**
   * C0000285: 薬師の助手ミミカ
   * 基本技：モンスター1体の状態異常を回復。
   * ※トリガー（ライフ回復時SPアクティブ）は lightCards.js で実装
   */
  C0000285: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, opponentField, setOpponentField } = getPlayerContext(context);

    // 基本技：モンスター1体の状態異常を回復
    if (context.skillType === 'basic') {
      // 自分と相手の場の両方からターゲットを選べる
      const allValidTargets: { monster: MonsterInstance; index: number; isOpponent: boolean }[] = [];

      // 自分の場
      myField.forEach((monster, idx) => {
        if (monster && monster.statusEffects && monster.statusEffects.length > 0) {
          allValidTargets.push({ monster, index: idx, isOpponent: false });
        }
      });

      // 相手の場
      opponentField.forEach((monster, idx) => {
        if (monster && monster.statusEffects && monster.statusEffects.length > 0) {
          allValidTargets.push({ monster, index: idx, isOpponent: true });
        }
      });

      if (allValidTargets.length === 0) {
        addLog('状態異常を持つモンスターがいません', 'info');
        return true;
      }

      const clearStatusEffects = (targetIndex: number, isOpponent: boolean) => {
        const setField = isOpponent ? setOpponentField : setMyField;
        const fieldArray = isOpponent ? opponentField : myField;
        const targetMonster = fieldArray[targetIndex];

        if (!targetMonster) return;

        setField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            return {
              ...m,
              statusEffects: [], // 状態異常を全てクリア
            };
          }
          return m;
        }));
        addLog(`薬師の助手ミミカの基本技: ${targetMonster.name}の状態異常を回復！`, 'heal');
      };

      // 1体のみの場合は自動選択
      if (allValidTargets.length === 1) {
        const target = allValidTargets[0];
        clearStatusEffects(target.index, target.isOpponent);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '状態異常を回復するモンスターを選択',
          targetPlayer: 'both',
          callback: (selectedIndex: number, isOpponentTarget?: boolean) => {
            clearStatusEffects(selectedIndex, isOpponentTarget ?? false);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック: 最初の自分のターゲット、なければ相手
      const selfTarget = allValidTargets.find(t => !t.isOpponent);
      const target = selfTarget || allValidTargets[0];
      clearStatusEffects(target.index, target.isOpponent);
      return true;
    }

    return false;
  },

  /**
   * C0000287: 癒しの猛毒（光属性魔法）
   * 自分のライフを1000回復し、相手プレイヤーに500ダメージを与える。
   */
  C0000287: (skillText, context) => {
    const { addLog } = context;
    const { setMyLife, setOpponentLife } = getPlayerContext(context);

    // 魔法効果
    // 自分のライフを1000回復
    setMyLife(prev => prev + 1000);
    addLog('癒しの猛毒: 自分のライフを1000回復！', 'heal');

    // 相手に500ダメージ
    setOpponentLife(prev => prev - 500);
    addLog('癒しの猛毒: 相手に500ダメージ！', 'damage');

    return true;
  },

  /**
   * C0000319: 嵐光の騎士
   * 基本技：自身の攻撃力の半分のダメージを相手モンスターに与える
   */
  C0000319: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('モンスターが存在しません', 'info');
        return false;
      }

      // ダメージ量 = 攻撃力の半分
      const damage = Math.floor((monster.currentAttack ?? monster.attack ?? 0) / 2);

      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true;
      }

      const applyDamage = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            const newHp = target.currentHp - damage;
            addLog(`嵐光の騎士の基本技: ${target.name}に${damage}ダメージ！`, 'damage');
            if (newHp <= 0) {
              addLog(`${target.name}は破壊された！`, 'damage');
              setOpponentGraveyard(prev => [...prev, target]);
              newField[targetIndex] = null;
            } else {
              newField[targetIndex] = { ...target, currentHp: newHp };
            }
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        applyDamage(validTargets[0].index);
        return true;
      }

      // 複数の場合はターゲット選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: `${damage}ダメージを与える相手モンスターを選択`,
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            applyDamage(targetIndex);
          },
          selectionOwner: context.currentPlayer,
        });
      }
      return true;
    }
    return false;
  },

  /**
   * C0000320: 雷帝ヴォルトロン
   * 【召喚時】相手の場にいるモンスター1体を破壊し、その攻撃力の半分のダメージを相手モンスター1体に与える
   */
  C0000320: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (skillText.includes('【召喚時】')) {
      // 相手フィールドのモンスターをチェック
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true;
      }

      const destroyAndDamage = (destroyIndex: number) => {
        const destroyedMonster = opponentField[destroyIndex];
        if (!destroyedMonster) return;

        const damage = Math.floor((destroyedMonster.attack ?? 0) / 2);

        // 破壊処理
        setOpponentField(prev => {
          const newField = [...prev];
          newField[destroyIndex] = null;
          return newField;
        });
        setOpponentGraveyard(prev => [...prev, destroyedMonster]);
        addLog(`雷帝ヴォルトロンの召喚時効果: ${destroyedMonster.name}を破壊！`, 'damage');

        // 残りのモンスターにダメージ
        const remainingTargets = opponentField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(t => t.monster !== null && t.index !== destroyIndex);

        if (remainingTargets.length === 0) {
          addLog(`追加ダメージの対象がいません`, 'info');
          return;
        }

        const applyDamage = (targetIndex: number) => {
          setOpponentField(prev => {
            const newField = [...prev];
            const target = newField[targetIndex];
            if (target) {
              const newHp = target.currentHp - damage;
              addLog(`${target.name}に${damage}ダメージ！`, 'damage');
              if (newHp <= 0) {
                addLog(`${target.name}は破壊された！`, 'damage');
                setOpponentGraveyard(prev => [...prev, target]);
                newField[targetIndex] = null;
              } else {
                newField[targetIndex] = { ...target, currentHp: newHp };
              }
            }
            return newField;
          });
        };

        if (remainingTargets.length === 1) {
          applyDamage(remainingTargets[0].index);
        } else if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: `${damage}ダメージを与える相手モンスターを選択`,
            targetPlayer: 'opponent',
            callback: applyDamage,
            selectionOwner: context.currentPlayer,
          });
        }
      };

      // 1体のみの場合は自動選択
      if (validTargets.length === 1) {
        destroyAndDamage(validTargets[0].index);
        return true;
      }

      // 複数の場合は破壊対象を選択
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: `破壊する相手モンスターを選択`,
          targetPlayer: 'opponent',
          callback: destroyAndDamage,
          selectionOwner: context.currentPlayer,
        });
      }
      return true;
    }
    return false;
  },

  /**
   * C0000327: 雷嵐龍サンダーストーム・レックス
   * 【召喚時】相手の場にいるモンスター1体を「雷撃」状態にする
   * 基本技：相手モンスター全体に600ダメージを与える
   * 上級技：SP1消費して相手モンスター全体に1200ダメージを与える
   */
  C0000327: (skillText, context) => {
    const { addLog, monsterIndex, setPendingMonsterTarget } = context;
    const { myField, myActiveSP, setMyActiveSP, setMyRestedSP, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 召喚時効果
    if (skillText.includes('【召喚時】')) {
      const validTargets = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(t => t.monster !== null);

      if (validTargets.length === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
        return true;
      }

      const applyThunder = (targetIndex: number) => {
        setOpponentField(prev => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            // 雷撃状態を付与（ATK-500、技不能）
            const statusEffects = target.statusEffects || [];
            statusEffects.push({
              type: 'THUNDER',
              duration: 1,
              source: 'C0000327',
              sourceName: '雷嵐龍サンダーストーム・レックス',
            });
            newField[targetIndex] = {
              ...target,
              statusEffects,
              currentAttack: Math.max(0, (target.currentAttack ?? target.attack ?? 0) - 500),
            };
            addLog(`${target.name}を「雷撃」状態にした！（ATK-500、技不能）`, 'damage');
          }
          return newField;
        });
      };

      if (validTargets.length === 1) {
        applyThunder(validTargets[0].index);
        return true;
      }

      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: `「雷撃」状態にする相手モンスターを選択`,
          targetPlayer: 'opponent',
          callback: applyThunder,
          selectionOwner: context.currentPlayer,
        });
      }
      return true;
    }

    // 基本技：相手モンスター全体に600ダメージ
    if (context.skillType === 'basic') {
      const damage = 600;
      let hitCount = 0;

      setOpponentField(prev => {
        const newField = [...prev];
        for (let i = 0; i < newField.length; i++) {
          const target = newField[i];
          if (target) {
            hitCount++;
            const newHp = target.currentHp - damage;
            if (newHp <= 0) {
              addLog(`${target.name}に${damage}ダメージ！破壊！`, 'damage');
              setOpponentGraveyard(prev => [...prev, target]);
              newField[i] = null;
            } else {
              newField[i] = { ...target, currentHp: newHp };
              addLog(`${target.name}に${damage}ダメージ！`, 'damage');
            }
          }
        }
        return newField;
      });

      if (hitCount === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
      } else {
        addLog(`雷嵐龍の基本技: 相手モンスター全体に${damage}ダメージ！`, 'damage');
      }
      return true;
    }

    // 上級技：SP1消費して相手モンスター全体に1200ダメージ
    if (context.skillType === 'advanced') {
      if (myActiveSP < 1) {
        addLog('SPが足りません（1必要）', 'info');
        return false;
      }

      setMyActiveSP(prev => prev - 1);
      setMyRestedSP(prev => prev + 1);

      const damage = 1200;
      let hitCount = 0;

      setOpponentField(prev => {
        const newField = [...prev];
        for (let i = 0; i < newField.length; i++) {
          const target = newField[i];
          if (target) {
            hitCount++;
            const newHp = target.currentHp - damage;
            if (newHp <= 0) {
              addLog(`${target.name}に${damage}ダメージ！破壊！`, 'damage');
              setOpponentGraveyard(prev => [...prev, target]);
              newField[i] = null;
            } else {
              newField[i] = { ...target, currentHp: newHp };
              addLog(`${target.name}に${damage}ダメージ！`, 'damage');
            }
          }
        }
        return newField;
      });

      if (hitCount === 0) {
        addLog('相手フィールドにモンスターがいません', 'info');
      } else {
        addLog(`雷嵐龍の上級技: SP1消費、相手モンスター全体に${damage}ダメージ！`, 'damage');
      }
      return true;
    }
    return false;
  },

  // ========================================
  // ご主人様・リリカ関連カード
  // ========================================

  /**
   * C0000241: ご主人様
   * 【召喚時】SP2を消費してデッキまたは墓地から《リリカ》と名の付くモンスター1体を場に召喚する（攻撃力50％、HP50％）。
   * 基本技：《リリカ》と名の付くモンスター1体の攻撃力を500アップ。
   * ※召喚時効果はトリガーで実装済み
   */
  C0000241: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 場の《リリカ》モンスターを取得
      const lilikaMonsters = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster && monster.name && monster.name.includes('リリカ'));

      if (lilikaMonsters.length === 0) {
        addLog('ご主人様の基本技: 場に《リリカ》モンスターがいません', 'info');
        return false;
      }

      const applyBuff = (targetIndex: number) => {
        setMyField(prev => prev.map((m, idx) => {
          if (idx === targetIndex && m) {
            const newAtk = (m.currentAttack ?? m.attack ?? 0) + 500;
            addLog(`ご主人様の基本技: ${m.name}の攻撃力+500！（${m.currentAttack || m.attack} → ${newAtk}）`, 'info');
            return { ...m, currentAttack: newAtk };
          }
          return m;
        }));
      };

      // 1体のみの場合は自動選択
      if (lilikaMonsters.length === 1) {
        applyBuff(lilikaMonsters[0].index);
        return true;
      }

      // 複数の場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '攻撃力をアップする《リリカ》を選択',
          targetPlayer: 'self',
          validTargets: lilikaMonsters.map(t => t.index),
          callback: (selectedIndex: number) => {
            applyBuff(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      applyBuff(lilikaMonsters[0].index);
      return true;
    }

    return false;
  },

  // ========================================
  // 雷・嵐系魔法カード
  // ========================================

  /**
   * C0000323: 雷鳴の裁き（光属性魔法）
   * 相手モンスター1体の攻撃力を1200ダウンし、攻撃不能にする。
   * 場に光属性モンスターがいれば、相手モンスター全体に300ダメージ。
   */
  C0000323: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手モンスターを検索
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('雷鳴の裁き: 相手モンスターがいません', 'info');
      return false;
    }

    const executeEffect = (targetIndex: number) => {
      // 対象モンスターの攻撃力-1200、攻撃不能
      setOpponentField(prev => {
        const newField = [...prev];
        if (newField[targetIndex]) {
          const target = newField[targetIndex];
          const newAtk = Math.max(0, (target.currentAttack ?? target.attack ?? 0) - 1200);
          newField[targetIndex] = {
            ...target,
            currentAttack: newAtk,
            canAttack: false,
            attackDisabled: true,
          };
          addLog(`雷鳴の裁き: ${target.name}の攻撃力-1200、攻撃不能！`, 'damage');
        }
        return newField;
      });

      // 場に光属性モンスターがいるかチェック
      const hasLightMonster = myField.some(m => m && m.attribute === '光');
      if (hasLightMonster) {
        const destroyedMonsters: MonsterInstance[] = [];
        setOpponentField(prev => {
          return prev.map(monster => {
            if (monster) {
              const newHp = monster.currentHp - 300;
              if (newHp <= 0) {
                destroyedMonsters.push(monster);
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        if (destroyedMonsters.length > 0) {
          setOpponentGraveyard(prev => [...prev, ...destroyedMonsters]);
        }
        addLog(`雷鳴の裁き: 光属性モンスターがいるため、相手全体に300ダメージ！`, 'damage');
      }
    };

    // 1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      executeEffect(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力-1200＆攻撃不能にするモンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          executeEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    executeEffect(opponentMonsters[0].index);
    return true;
  },

  /**
   * C0000324: 嵐の咆哮（光属性魔法）
   * 自分の場の光属性モンスター数×400ダメージを相手プレイヤーに与え、
   * 自分の光属性モンスター全体の攻撃力を300アップする。
   */
  C0000324: (skillText, context) => {
    const { addLog } = context;
    const { myField, setMyField, setOpponentLife } = getPlayerContext(context);

    // 場の光属性モンスターをカウント
    const lightCount = myField.filter(m => m && m.attribute === '光').length;

    if (lightCount === 0) {
      addLog('嵐の咆哮: 場に光属性モンスターがいないため効果なし', 'info');
      return false;
    }

    // 光属性モンスター数×400ダメージを相手に与える
    const damage = lightCount * 400;
    setOpponentLife(prev => prev - damage);
    addLog(`嵐の咆哮: 光属性モンスター${lightCount}体×400＝${damage}ダメージ！`, 'damage');

    // 光属性モンスター全体の攻撃力+300
    let buffedCount = 0;
    setMyField(prev => {
      return prev.map(monster => {
        if (monster && monster.attribute === '光') {
          buffedCount++;
          return {
            ...monster,
            currentAttack: (monster.currentAttack ?? monster.attack ?? 0) + 300,
          };
        }
        return monster;
      });
    });

    if (buffedCount > 0) {
      addLog(`嵐の咆哮: 光属性モンスター${buffedCount}体の攻撃力+300！`, 'info');
    }

    return true;
  },

  /**
   * C0000325: 電光の浄化（光属性魔法）
   * 相手モンスター1体の効果を無効化する。
   * 場に光属性モンスターがいれば、自分のライフを800回復する。
   */
  C0000325: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, opponentField, setOpponentField, setMyLife } = getPlayerContext(context);

    // 相手モンスターを検索
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('電光の浄化: 相手モンスターがいません', 'info');
      return false;
    }

    const executeEffect = (targetIndex: number) => {
      // 対象モンスターの効果を無効化（SILENCE状態異常を付与）
      setOpponentField(prev => {
        const newField = [...prev];
        if (newField[targetIndex]) {
          const target = newField[targetIndex];
          statusEffectEngine.applyStatus(target, STATUS_EFFECT_TYPES.SILENCE, {
            duration: 0, // このターン終了時まで
            source: 'C0000325',
            sourceName: '電光の浄化',
          });
          newField[targetIndex] = { ...target };
          addLog(`電光の浄化: ${target.name}の効果を無効化！`, 'info');
        }
        return newField;
      });

      // 場に光属性モンスターがいればライフ800回復
      const hasLightMonster = myField.some(m => m && m.attribute === '光');
      if (hasLightMonster) {
        setMyLife(prev => prev + 800);
        addLog('電光の浄化: 光属性モンスターがいるため、ライフを800回復！', 'heal');
      }
    };

    // 1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      executeEffect(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '効果を無効化するモンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          executeEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    executeEffect(opponentMonsters[0].index);
    return true;
  },

  /**
   * C0000248: 輝鎖の聖姫ルミリア
   * 【召喚時】場にいる光属性モンスター1体につき相手に500ダメージを与え、ターン終了時まで相手モンスター全体の攻撃力を半分にする。
   * 【常時】場に『鎖縛の幻姫リアノン』または『鎖縛の禁忌姫リアノン・エターナル』がいる場合、このカードの攻撃力を1000アップする。
   * 基本技：自分のライフを1000回復する。
   * ※召喚時効果・常時効果はトリガーで実装済み
   */
  C0000248: (skillText, context) => {
    const { addLog } = context;

    if (context.skillType === 'basic') {
      // 自分のライフを1000回復
      healLife(context, 1000, true);
      addLog('輝鎖の聖姫ルミリアの基本技: ライフを1000回復！', 'heal');
      return true;
    }

    return false;
  },

  /**
   * C0000350: 鳥民の祈り（魔法カード）
   * 自分のライフを800回復し、デッキから《アヴィクルス》フィールドカード1枚を手札に加える。
   */
  C0000350: (skillText, context) => {
    const { addLog, setPendingDeckReview } = context;
    const { myDeck, setMyDeck, setMyHand, setMyLife } = getPlayerContext(context);

    // ライフ800回復
    setMyLife(prev => prev + 800);
    addLog('鳥民の祈り: ライフを800回復！', 'heal');

    // デッキから《アヴィクルス》フィールドカードを探す
    const avicrusFieldCards = myDeck.filter(card =>
      !!card.name && card.name.includes('アヴィクルス') && card.type === 'field'
    );

    if (avicrusFieldCards.length === 0) {
      addLog('鳥民の祈り: デッキに《アヴィクルス》フィールドカードがありません', 'info');
      return true;
    }

    // 1枚のみの場合は自動選択
    if (avicrusFieldCards.length === 1) {
      const selectedCard = avicrusFieldCards[0];
      setMyHand(prev => [...prev, selectedCard]);
      setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
      addLog(`鳥民の祈り: 「${selectedCard.name}」を手札に加えた！`, 'info');
      return true;
    }

    // 複数ある場合はデッキ確認UIを使用
    if (setPendingDeckReview) {
      setPendingDeckReview({
        cards: avicrusFieldCards,
        message: '手札に加える《アヴィクルス》フィールドカードを選択',
        count: 1,
        callback: (selectedCards: CardWithId[]) => {
          if (selectedCards && selectedCards.length > 0) {
            const selectedCard = selectedCards[0];
            setMyHand(prev => [...prev, selectedCard]);
            setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
            addLog(`鳥民の祈り: 「${selectedCard.name}」を手札に加えた！`, 'info');
          }
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    // フォールバック
    const selectedCard = avicrusFieldCards[0];
    setMyHand(prev => [...prev, selectedCard]);
    setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
    addLog(`鳥民の祈り: 「${selectedCard.name}」を手札に加えた！`, 'info');
    return true;
  },

  /**
   * C0000374: 虹羽密林の輝甲虫・ルクセリオ
   * 基本技: このカードを墓地に送り、デッキから《虹羽密林》カード1枚を手札に加える
   */
  C0000374: (skillText, context) => {
    const { addLog, monsterIndex, setPendingDeckReview } = context;
    const { myField, setMyField, setMyGraveyard, myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('ルクセリオの基本技: 対象が見つかりません', 'info');
        return false;
      }

      // デッキに《虹羽密林》カードがあるか確認
      const rainbowCards = myDeck.filter(card =>
        !!card.name && card.name.includes('虹羽密林')
      );

      if (rainbowCards.length === 0) {
        addLog('ルクセリオの基本技: デッキに《虹羽密林》カードがありません', 'info');
        return false;
      }

      // 自身を墓地に送る
      setMyField(prev => {
        const newField = [...prev];
        newField[monsterIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, monster]);
      addLog('ルクセリオは自身を墓地に送った！', 'info');

      // 1枚のみの場合は自動選択
      if (rainbowCards.length === 1) {
        const selectedCard = rainbowCards[0];
        setMyHand(prev => [...prev, selectedCard]);
        setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
        addLog(`ルクセリオの基本技: 「${selectedCard.name}」を手札に加えた！`, 'info');
        return true;
      }

      // 複数ある場合はデッキ確認UIを使用
      if (setPendingDeckReview) {
        setPendingDeckReview({
          cards: rainbowCards,
          message: '手札に加える《虹羽密林》カードを選択',
          count: 1,
          callback: (selectedCards: CardWithId[]) => {
            if (selectedCards && selectedCards.length > 0) {
              const selectedCard = selectedCards[0];
              setMyHand(prev => [...prev, selectedCard]);
              setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
              addLog(`ルクセリオの基本技: 「${selectedCard.name}」を手札に加えた！`, 'info');
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      // フォールバック
      const selectedCard = rainbowCards[0];
      setMyHand(prev => [...prev, selectedCard]);
      setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
      addLog(`ルクセリオの基本技: 「${selectedCard.name}」を手札に加えた！`, 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000362: 風鳴樹
   * 【召喚時】自分のSPトークンを1個レスト状態からアクティブに変える
   * 基本技: 自分の手札1枚を墓地に送り、デッキから【プラント】カード1枚を手札に加える
   */
  C0000362: (skillText, context) => {
    const { addLog, setPendingHandSelection, setPendingDeckReview } = context;
    const { myHand, setMyHand, setMyGraveyard, myDeck, setMyDeck, myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

    // 召喚時効果
    if (skillText.includes('【召喚時】')) {
      if (myRestedSP > 0) {
        setMyRestedSP(prev => prev - 1);
        setMyActiveSP(prev => prev + 1);
        addLog('風鳴樹の召喚時効果: SPを1個アクティブに変えた！', 'info');
        return true;
      } else {
        addLog('風鳴樹の召喚時効果: レスト状態のSPがありません', 'info');
        return false;
      }
    }

    // 基本技: 手札1枚捨て→プラントサーチ
    if (context.skillType === 'basic') {
      if (myHand.length === 0) {
        addLog('風鳴樹の基本技: 手札がありません', 'info');
        return false;
      }

      // デッキにプラントがあるか確認
      const plantCards = myDeck.filter(card =>
        card.category && (
          (Array.isArray(card.category) && card.category.some(c => c.includes('プラント'))) ||
          (typeof card.category === 'string' && card.category.includes('プラント'))
        )
      );

      if (plantCards.length === 0) {
        addLog('風鳴樹の基本技: デッキに【プラント】カードがありません', 'info');
        return false;
      }

      const performSearch = (discardedCard: CardWithId) => {
        // 手札から墓地へ
        setMyHand(prev => prev.filter(c => c.uniqueId !== discardedCard.uniqueId));
        setMyGraveyard(prev => [...prev, discardedCard]);
        addLog(`風鳴樹の基本技: 「${discardedCard.name}」を墓地に送った`, 'info');

        // プラントサーチ
        if (plantCards.length === 1) {
          const searchedCard = plantCards[0];
          setMyHand(prev => [...prev, searchedCard]);
          setMyDeck(prev => prev.filter(c => c.uniqueId !== searchedCard.uniqueId));
          addLog(`風鳴樹の基本技: 「${searchedCard.name}」を手札に加えた！`, 'info');
        } else if (setPendingDeckReview) {
          setPendingDeckReview({
            cards: plantCards,
            message: '手札に加える【プラント】カードを選択',
            count: 1,
            callback: (selectedCards: CardWithId[]) => {
              if (selectedCards && selectedCards.length > 0) {
                const searchedCard = selectedCards[0];
                setMyHand(prev => [...prev, searchedCard]);
                setMyDeck(prev => prev.filter(c => c.uniqueId !== searchedCard.uniqueId));
                addLog(`風鳴樹の基本技: 「${searchedCard.name}」を手札に加えた！`, 'info');
              }
            },
            selectionOwner: context.currentPlayer,
          });
        } else {
          const searchedCard = plantCards[0];
          setMyHand(prev => [...prev, searchedCard]);
          setMyDeck(prev => prev.filter(c => c.uniqueId !== searchedCard.uniqueId));
          addLog(`風鳴樹の基本技: 「${searchedCard.name}」を手札に加えた！`, 'info');
        }
      };

      // 手札選択
      if (myHand.length === 1) {
        performSearch(myHand[0]);
        return true;
      }

      if (setPendingHandSelection) {
        setPendingHandSelection({
          message: '墓地に送る手札を選択',
          count: 1,
          callback: (selectedCards: CardWithId[]) => {
            if (selectedCards && selectedCards.length > 0) {
              performSearch(selectedCards[0]);
            }
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      performSearch(myHand[0]);
      return true;
    }

    return false;
  },

  /**
   * C0000365: ヴォランティス・オーブ
   * 自分の場の《ヴォランティス》モンスター1体を選択し、その攻撃力を2500アップ
   * このターン中、相手モンスターを戦闘で破壊した場合、追加で1000ダメージを相手プレイヤーに与える
   * 場に《天翔秘島》フィールドカードがある場合、このターン中、そのモンスターの超過分の戦闘ダメージは相手プレイヤーに与えられる（貫通）
   */
  C0000365: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField, myFieldCard } = getPlayerContext(context);

    // 場のヴォランティスモンスターを取得
    const volantisMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster && monster.name && monster.name.includes('ヴォランティス'));

    if (volantisMonsters.length === 0) {
      addLog('ヴォランティス・オーブ: 場に《ヴォランティス》モンスターがいません', 'info');
      return false;
    }

    // 天翔秘島があるかチェック
    const hasTenshouHitou = !!(myFieldCard && myFieldCard.name && myFieldCard.name.includes('天翔秘島'));

    const applyBuff = (targetIndex: number) => {
      setMyField(prev => prev.map((m, idx) => {
        if (idx === targetIndex && m) {
          const newAtk = (m.currentAttack ?? m.attack ?? 0) + 2500;
          addLog(`ヴォランティス・オーブ: ${m.name}の攻撃力+2500！`, 'heal');
          return {
            ...m,
            currentAttack: newAtk,
            attackBuffUntilEndOfTurn: (m.attackBuffUntilEndOfTurn || 0) + 2500,
            orbBonusDamage: 1000, // 戦闘破壊時1000ダメージ
            hasPiercing: hasTenshouHitou, // 天翔秘島がある場合は貫通
          };
        }
        return m;
      }));

      if (hasTenshouHitou) {
        addLog('天翔秘島の力: このターン中、超過ダメージが貫通する！', 'info');
      }
    };

    // 1体のみの場合は自動選択
    if (volantisMonsters.length === 1) {
      applyBuff(volantisMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '攻撃力+2500する《ヴォランティス》モンスターを選択',
        targetPlayer: 'self',
        validTargets: volantisMonsters.map(v => v.index),
        callback: (selectedIndex: number) => {
          applyBuff(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    applyBuff(volantisMonsters[0].index);
    return true;
  },

  /**
   * C0000372: 天翼の召喚儀式
   * 手札から《ヴォランティス・オーブ》を1枚墓地に送り、場の《鳥民》モンスター1体をリリースして
   * 対応する《ヴォランティス》モンスター1体をデッキから召喚（コスト無視）
   * 裁定の鳥民・ミナルス → ヴォランティス・アルディオン
   * 炎翼の鳥民・イグニス → ヴォランティス・インフェルノ
   * 影羽の鳥民・ノクティス → ヴォランティス・エクリプス
   * 風詠の鳥民・ゼフィラス → ヴォランティス・テンペスト
   */
  C0000372: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, registerCardTriggers, continuousEffectEngine, summoningSickness = true } = context;
    const { myHand, setMyHand, myField, setMyField, myDeck, setMyDeck, setMyGraveyard, currentPlayer } = getPlayerContext(context);

    // 対応表
    const summonMap = {
      '裁定の鳥民・ミナルス': 'ヴォランティス・アルディオン',
      '炎翼の鳥民・イグニス': 'ヴォランティス・インフェルノ',
      '影羽の鳥民・ノクティス': 'ヴォランティス・エクリプス',
      '風詠の鳥民・ゼフィラス': 'ヴォランティス・テンペスト',
    };

    // 手札にヴォランティス・オーブがあるか確認
    const orbInHand = myHand.find(card => card.name === 'ヴォランティス・オーブ');
    if (!orbInHand) {
      addLog('天翼の召喚儀式: 手札に《ヴォランティス・オーブ》がありません', 'info');
      return false;
    }

    // 場に対象の鳥民がいるか確認
    const validTribunes = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => {
        if (!monster || !monster.name) return false;
        return Object.keys(summonMap).some(name => monster.name.includes(name));
      });

    if (validTribunes.length === 0) {
      addLog('天翼の召喚儀式: 場に対象の《鳥民》モンスターがいません', 'info');
      return false;
    }

    const performRitual = (tribuneIndex: number) => {
      const tribune = myField[tribuneIndex];
      if (!tribune) return;

      // 対応するヴォランティスを特定
      if (!tribune.name) {
        console.warn('[light.js 天翼の召喚儀式] フィールドにnameがないモンスターがいます:', {
          tribuneIndex,
          tribuneKeys: Object.keys(tribune),
          tribune,
          sourceCard: context.card?.name,
        });
        return;
      }
      let targetVolantis = null;
      for (const [tribuneName, volantisName] of Object.entries(summonMap)) {
        if (tribune.name.includes(tribuneName)) {
          targetVolantis = volantisName;
          break;
        }
      }

      if (!targetVolantis) {
        addLog('天翼の召喚儀式: 対応するヴォランティスが見つかりません', 'info');
        return;
      }

      // デッキにそのヴォランティスがいるか確認
      const volantisCard = myDeck.find(card => card.name === targetVolantis);
      if (!volantisCard) {
        addLog(`天翼の召喚儀式: デッキに《${targetVolantis}》がいません`, 'info');
        return;
      }

      // オーブを墓地に送る
      setMyHand(prev => prev.filter(c => c.uniqueId !== orbInHand.uniqueId));
      setMyGraveyard(prev => [...prev, orbInHand]);
      addLog('天翼の召喚儀式: 《ヴォランティス・オーブ》を墓地に送った', 'info');

      // 鳥民をリリース
      setMyField(prev => {
        const newField = [...prev];
        newField[tribuneIndex] = null;
        return newField;
      });
      setMyGraveyard(prev => [...prev, tribune]);
      addLog(`天翼の召喚儀式: 「${tribune.name}」をリリースした`, 'info');

      // デッキから除去
      setMyDeck(prev => prev.filter(c => c.uniqueId !== volantisCard.uniqueId));

      // ヴォランティスを召喚
      setMyField(prev => {
        const newField = [...prev];
        const slot = newField.findIndex(s => s === null);
        if (slot !== -1) {
          const summonedMonster: MonsterInstance = {
            ...volantisCard,
            currentHp: volantisCard.hp ?? 0,
            currentAttack: volantisCard.attack ?? 0,
            canAttack: !summoningSickness, // 召喚酔い設定に依存
            charges: [],
            statusEffects: [],
            usedSkillThisTurn: false,
            owner: currentPlayer,
            effectImmuneUntilNextTurnEnd: true, // 次のターン終了時まで効果無効化されない
          };
          newField[slot] = summonedMonster;

          // トリガー登録
          if (registerCardTriggers) {
            registerCardTriggers(summonedMonster, currentPlayer, slot);
          }

          // 常時効果登録
          if (continuousEffectEngine) {
            continuousEffectEngine.register(summonedMonster, currentPlayer);
          }
        }
        return newField;
      });

      addLog(`天翼の召喚儀式: デッキから《${targetVolantis}》を特殊召喚！`, 'info');
    };

    // 1体のみの場合は自動選択
    if (validTribunes.length === 1) {
      performRitual(validTribunes[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: 'リリースする《鳥民》モンスターを選択',
        targetPlayer: 'self',
        validTargets: validTribunes.map(t => t.index),
        callback: (selectedIndex: number) => {
          performRitual(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    performRitual(validTribunes[0].index);
    return true;
  },

  /**
   * C0000397: 呪術狩りの聖焔騎士レオノーラ
   * 基本技: 自分の墓地の光属性カード1枚をデッキに戻し、相手の闇属性モンスター1体に1200ダメージ
   *         （ターンに1度、闇以外なら600ダメージ）
   */
  C0000397: (skillText, context) => {
    const { addLog, setPendingMonsterTarget, setPendingGraveyardSelection } = context;
    const { myGraveyard, setMyGraveyard, setMyDeck, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 墓地に光属性カードがあるか確認
      const lightCardsInGraveyard = myGraveyard.filter(c => c.attribute === '光');

      if (lightCardsInGraveyard.length === 0) {
        addLog('呪術狩りの聖焔騎士レオノーラの基本技: 墓地に光属性カードがありません', 'info');
        return false;
      }

      // 相手モンスターがいるか確認
      const opponentMonsters = opponentField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => monster !== null);

      if (opponentMonsters.length === 0) {
        addLog('呪術狩りの聖焔騎士レオノーラの基本技: 相手モンスターがいません', 'info');
        return false;
      }

      const executeEffect = (selectedCard: CardWithId, targetIndex: number) => {
        // 墓地から除去してデッキに戻す
        setMyGraveyard(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
        setMyDeck(prev => {
          const newDeck = [...prev, selectedCard];
          // シャッフル
          for (let i = newDeck.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
          }
          return newDeck;
        });
        addLog(`${selectedCard.name}をデッキに戻した`, 'info');

        // ダメージ計算（闇属性なら1200、それ以外なら600）
        const target = opponentField[targetIndex];
        if (!target) return;
        const isDark = target.attribute === '闇';
        const damage = isDark ? 1200 : 600;

        const newHp = target.currentHp - damage;
        addLog(`呪術狩りの聖焔騎士レオノーラの基本技: ${target.name}に${damage}ダメージ！${isDark ? '（闇属性）' : ''}`, 'damage');

        if (newHp <= 0) {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
          setOpponentGraveyard(prev => [...prev, target as unknown as CardWithId]);
          addLog(`${target.name}は破壊された！`, 'damage');
        } else {
          setOpponentField(prev => {
            const newField = [...prev];
            newField[targetIndex] = { ...target, currentHp: newHp };
            return newField;
          });
        }
      };

      // 墓地からカード選択
      const selectGraveyardCard = (callback: (card: CardWithId) => void) => {
        if (lightCardsInGraveyard.length === 1) {
          callback(lightCardsInGraveyard[0]);
          return;
        }

        if (setPendingGraveyardSelection) {
          setPendingGraveyardSelection({
            message: 'デッキに戻す光属性カードを選択',
            cards: lightCardsInGraveyard,
            callback: (selectedCards: CardWithId[]) => {
              if (selectedCards[0]) callback(selectedCards[0]);
            },
            selectionOwner: context.currentPlayer,
          });
        } else {
          callback(lightCardsInGraveyard[0]);
        }
      };

      // モンスター選択
      const selectTarget = (selectedCard: CardWithId) => {
        if (opponentMonsters.length === 1) {
          executeEffect(selectedCard, opponentMonsters[0].index);
          return;
        }

        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: 'ダメージを与える相手モンスターを選択',
            targetPlayer: 'opponent',
            callback: (targetIndex: number) => {
              executeEffect(selectedCard, targetIndex);
            },
            selectionOwner: context.currentPlayer,
          });
        } else {
          executeEffect(selectedCard, opponentMonsters[0].index);
        }
      };

      selectGraveyardCard(selectTarget);
      return true;
    }

    return false;
  },

  /**
   * C0000433: 灰光の裁き
   * 相手モンスター1体に800ダメージを与える。そのモンスターが闇属性の場合、このダメージを1600に変更。
   */
  C0000433: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

    // 相手モンスターを確認
    const opponentMonsters = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (opponentMonsters.length === 0) {
      addLog('灰光の裁き: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    // 効果を適用する関数
    const applyEffect = (targetIndex: number) => {
      let destroyedMonster: MonsterInstance | null = null;

      setOpponentField((prev) => {
        const newField = [...prev];
        const target = newField[targetIndex];

        if (target) {
          // 闇属性なら1600、それ以外は800
          const isDark = target.attribute === '闇';
          const damage = isDark ? 1600 : 800;
          const newHp = target.currentHp - damage;

          addLog(`灰光の裁き: ${target.name}に${damage}ダメージ！${isDark ? '（闇属性特効）' : ''}`, 'damage');

          if (newHp <= 0) {
            destroyedMonster = target;
            addLog(`${target.name}は破壊された！`, 'damage');
            newField[targetIndex] = null;
          } else {
            newField[targetIndex] = { ...target, currentHp: newHp };
          }
        }
        return newField;
      });

      if (destroyedMonster) {
        setOpponentGraveyard((prev) => [...prev, destroyedMonster as unknown as CardWithId]);
      }
    };

    // 1体のみの場合は自動選択
    if (opponentMonsters.length === 1) {
      applyEffect(opponentMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '対象の相手モンスターを選択',
        targetPlayer: 'opponent',
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    applyEffect(opponentMonsters[0].index);
    return true;
  },

  /**
   * C0000448: 聖域の白獅子レグナス
   * 【魔障壁】
   * 基本技: 自身に「守護」を付与する（守護：1度だけ受けるダメージを半減）
   */
  C0000448: (skillText, context) => {
    const { addLog, monsterIndex } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      const monster = myField[monsterIndex];
      if (!monster) {
        addLog('聖域の白獅子レグナス: カードが見つかりません', 'info');
        return false;
      }

      // 守護状態を付与（GUARD: 1度だけダメージ半減）
      setMyField(prev => {
        const newField = [...prev];
        if (newField[monsterIndex]) {
          const targetMonster = newField[monsterIndex];
          const newEffects = [...(targetMonster.statusEffects || [])];
          newEffects.push({
            type: 'GUARD',
            duration: -1, // 使用されるまで継続
            source: 'C0000448',
            sourceName: '聖域の白獅子レグナス',
          });
          newField[monsterIndex] = {
            ...targetMonster,
            statusEffects: newEffects,
          };
        }
        return newField;
      });

      addLog('聖域の白獅子レグナスの基本技: 守護を獲得！（1度だけダメージ半減）', 'info');
      return true;
    }

    return false;
  },

  /**
   * C0000549: 落雷
   * 相手モンスター1体に1000ダメージ。飛行モンスターの場合、代わりに破壊。
   */
  C0000549: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);
    const { hasHikou } = require('../keywordAbilities');

    // 相手フィールドにモンスターがいるか確認
    const validTargets = opponentField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => monster !== null);

    if (validTargets.length === 0) {
      addLog('落雷: 相手フィールドにモンスターがいません', 'info');
      return false;
    }

    const applyEffect = (targetIndex: number) => {
      const target = opponentField[targetIndex];
      if (!target) return;

      // 飛行モンスターかどうかで効果分岐
      if (hasHikou(target)) {
        // 飛行モンスターの場合は破壊
        setOpponentField((prev) => {
          const newField = [...prev];
          newField[targetIndex] = null;
          return newField;
        });
        setOpponentGraveyard((prev) => [...prev, target]);
        addLog(`落雷: 飛行モンスター「${target.name}」を撃墜！（破壊）`, 'damage');
      } else {
        // 飛行モンスターでない場合は1000ダメージ
        const newHp = target.currentHp - 1000;
        if (newHp <= 0) {
          setOpponentField((prev) => {
            const newField = [...prev];
            newField[targetIndex] = null;
            return newField;
          });
          setOpponentGraveyard((prev) => [...prev, target]);
          addLog(`落雷: ${target.name}に1000ダメージを与え破壊！`, 'damage');
        } else {
          setOpponentField((prev) => {
            const newField = [...prev];
            newField[targetIndex] = { ...target, currentHp: newHp };
            return newField;
          });
          addLog(`落雷: ${target.name}に1000ダメージ！（残りHP: ${newHp}）`, 'damage');
        }
      }
    };

    // 1体のみの場合は自動選択
    if (validTargets.length === 1) {
      applyEffect(validTargets[0].index);
      return true;
    }

    // 複数いる場合は選択
    setPendingMonsterTarget({
      message: '落雷の対象を選択（飛行なら破壊、それ以外は1000ダメージ）',
      targetPlayer: 'opponent',
      callback: (selectedIndex: number) => {
        applyEffect(selectedIndex);
      },
      selectionOwner: context.currentPlayer,
    });

    return true;
  },

  // ========================================
  // 彷徨い状態解除カード
  // ========================================

  /**
   * C0000572: 目覚めの歌い手
   * 基本技: 自分のモンスター1体の状態異常を回復。
   */
  C0000572: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    if (context.skillType === 'basic') {
      // 状態異常を持つ味方モンスターを検索
      const monstersWithStatus = myField
        .map((m, idx) => ({ monster: m, index: idx }))
        .filter(({ monster }) => {
          return monster && monster.statusEffects && monster.statusEffects.length > 0;
        });

      if (monstersWithStatus.length === 0) {
        addLog('目覚めの歌い手の基本技: 状態異常を持つ味方モンスターがいません', 'info');
        return false;
      }

      const removeAllStatus = (targetIndex: number) => {
        setMyField((prev) => {
          const newField = [...prev];
          const target = newField[targetIndex];
          if (target) {
            // 全ての状態異常を解除
            statusEffectEngine.removeStatus(target, null);
            // 攻撃力を元に戻す（彷徨い等で変更されていた場合）
            const originalAttack = target.attack ?? target.currentAttack ?? 0;
            newField[targetIndex] = {
              ...target,
              currentAttack: originalAttack,
              statusEffects: [],
            };
            addLog(`目覚めの歌い手の基本技: ${target.name}の状態異常を全て回復！`, 'heal');
          }
          return newField;
        });
      };

      // 1体のみの場合は自動選択
      if (monstersWithStatus.length === 1) {
        removeAllStatus(monstersWithStatus[0].index);
        return true;
      }

      // 複数いる場合は選択UI
      if (setPendingMonsterTarget) {
        setPendingMonsterTarget({
          message: '状態異常を回復する味方モンスターを選択',
          targetPlayer: 'self',
          validIndices: monstersWithStatus.map((m) => m.index),
          callback: (selectedIndex: number) => {
            removeAllStatus(selectedIndex);
          },
          selectionOwner: context.currentPlayer,
        });
        return true;
      }

      removeAllStatus(monstersWithStatus[0].index);
      return true;
    }

    return false;
  },

  /**
   * C0000574: 聖火の救済
   * 自分のモンスター1体の状態異常を回復し、HPを500回復。
   */
  C0000574: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 状態異常を持つ、またはHPが減っている味方モンスターを検索
    const validMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => {
        if (!monster) return false;
        const hasStatus = monster.statusEffects && monster.statusEffects.length > 0;
        const canHeal = monster.currentHp < (monster.maxHp ?? monster.hp ?? monster.currentHp);
        return hasStatus || canHeal;
      });

    if (validMonsters.length === 0) {
      addLog('聖火の救済: 対象となる味方モンスターがいません', 'info');
      return false;
    }

    const applyEffect = (targetIndex: number) => {
      setMyField((prev) => {
        const newField = [...prev];
        const target = newField[targetIndex];
        if (target) {
          // 状態異常を全て解除
          statusEffectEngine.removeStatus(target, null);
          // 攻撃力を元に戻す（彷徨い等で変更されていた場合）
          const originalAttack = target.attack ?? target.currentAttack ?? 0;
          // HPを500回復
          const maxHp = target.maxHp ?? target.hp ?? target.currentHp;
          const healAmount = Math.min(500, maxHp - target.currentHp);
          newField[targetIndex] = {
            ...target,
            currentAttack: originalAttack,
            currentHp: Math.min(target.currentHp + 500, maxHp),
            statusEffects: [],
          };
          addLog(`聖火の救済: ${target.name}の状態異常を回復し、HP${healAmount}回復！`, 'heal');
        }
        return newField;
      });
    };

    // 1体のみの場合は自動選択
    if (validMonsters.length === 1) {
      applyEffect(validMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '状態異常を回復しHPを500回復する味方モンスターを選択',
        targetPlayer: 'self',
        validIndices: validMonsters.map((m) => m.index),
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    applyEffect(validMonsters[0].index);
    return true;
  },

  /**
   * C0000575: 魂の帰還
   * 自分のモンスター1体の【彷徨い】状態を解除し、攻撃力を150%にする。
   */
  C0000575: (skillText, context) => {
    const { addLog, setPendingMonsterTarget } = context;
    const { myField, setMyField } = getPlayerContext(context);

    // 彷徨い状態の味方モンスターを検索
    const wanderingMonsters = myField
      .map((m, idx) => ({ monster: m, index: idx }))
      .filter(({ monster }) => {
        if (!monster) return false;
        return monster.statusEffects?.some(
          (s: { type: string }) => s.type === STATUS_EFFECT_TYPES.WANDERING
        );
      });

    if (wanderingMonsters.length === 0) {
      addLog('魂の帰還: 彷徨い状態の味方モンスターがいません', 'info');
      return false;
    }

    const applyEffect = (targetIndex: number) => {
      setMyField((prev) => {
        const newField = [...prev];
        const target = newField[targetIndex];
        if (target) {
          // 彷徨い状態を解除
          statusEffectEngine.removeStatus(target, STATUS_EFFECT_TYPES.WANDERING);
          // 攻撃力を元の150%にする
          const originalAttack = target.attack ?? 0;
          const boostedAttack = Math.floor(originalAttack * 1.5);
          newField[targetIndex] = {
            ...target,
            currentAttack: boostedAttack,
          };
          addLog(`魂の帰還: ${target.name}の【彷徨い】状態を解除！攻撃力が${boostedAttack}に上昇！`, 'heal');
        }
        return newField;
      });
    };

    // 1体のみの場合は自動選択
    if (wanderingMonsters.length === 1) {
      applyEffect(wanderingMonsters[0].index);
      return true;
    }

    // 複数いる場合は選択UI
    if (setPendingMonsterTarget) {
      setPendingMonsterTarget({
        message: '【彷徨い】状態を解除して攻撃力を150%にする味方モンスターを選択',
        targetPlayer: 'self',
        validIndices: wanderingMonsters.map((m) => m.index),
        callback: (selectedIndex: number) => {
          applyEffect(selectedIndex);
        },
        selectionOwner: context.currentPlayer,
      });
      return true;
    }

    applyEffect(wanderingMonsters[0].index);
    return true;
  },
};
